import { useState, useEffect, useRef } from 'react';
import PageHeader from '../components/PageHeader';
import api from '@/api/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, CheckCircle, Building2, Trash2, DollarSign, 
  Upload, Download, Link2, RefreshCw, FileSpreadsheet,
  ArrowUpCircle, ArrowDownCircle, Search, Filter, X,
  AlertCircle, Clock, Check, FileText, ArrowRightLeft, Pencil, Sparkles, RotateCcw, History, PlusCircle, Loader2
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import * as XLSX from 'xlsx';

// Belvo Connect Form Component
const BelvoConnectForm = ({ bankAccounts, onSuccess, onClose }) => {
  const [step, setStep] = useState('status'); // status, select, credentials
  const [belvoStatus, setBelvoStatus] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [selectedBankAccount, setSelectedBankAccount] = useState('');
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    checkBelvoStatus();
    loadConnections();
  }, []);

  const checkBelvoStatus = async () => {
    try {
      const res = await api.get('/belvo/status');
      setBelvoStatus(res.data);
      if (res.data.configured) {
        loadInstitutions();
      }
    } catch (error) {
      setBelvoStatus({ configured: false });
    }
  };

  const loadInstitutions = async () => {
    try {
      const res = await api.get('/belvo/institutions');
      setInstitutions(res.data.institutions || []);
    } catch (error) {
      console.log('Error loading institutions');
    }
  };

  const loadConnections = async () => {
    try {
      const res = await api.get('/belvo/connections');
      setConnections(res.data || []);
    } catch (error) {
      console.log('Error loading connections');
    }
  };

  const handleConnect = async () => {
    if (!selectedInstitution || !selectedBankAccount || !credentials.username || !credentials.password) {
      toast.error('Completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      await api.post('/belvo/connect', {
        institution_id: selectedInstitution.id,
        bank_account_id: selectedBankAccount,
        username: credentials.username,
        password: credentials.password
      });
      toast.success('Banco conectado exitosamente');
      loadConnections();
      setStep('status');
      setCredentials({ username: '', password: '' });
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error conectando banco');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (connectionId) => {
    setSyncing(true);
    try {
      const res = await api.post(`/belvo/sync/${connectionId}`);
      toast.success(res.data.message);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error sincronizando');
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteConnection = async (connectionId) => {
    if (!window.confirm('¿Eliminar esta conexión bancaria?')) return;
    try {
      await api.delete(`/belvo/connections/${connectionId}`);
      toast.success('Conexión eliminada');
      loadConnections();
    } catch (error) {
      toast.error('Error eliminando conexión');
    }
  };

  // Status check / Not configured
  if (!belvoStatus?.configured) {
    return (
      <div className="space-y-4 py-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Belvo no está configurado</p>
              <p className="text-sm text-yellow-600 mt-1">
                Para conectar bancos automáticamente, necesitas agregar tus credenciales de Belvo en el archivo .env:
              </p>
              <div className="mt-2 bg-gray-800 text-green-400 p-2 rounded text-xs font-mono">
                BELVO_SECRET_ID=&quot;tu_secret_id&quot;<br/>
                BELVO_SECRET_PASSWORD=&quot;tu_secret_password&quot;<br/>
                BELVO_ENV=&quot;sandbox&quot;
              </div>
              <a href="https://developers.belvo.com" target="_blank" rel="noreferrer" 
                className="text-blue-600 text-sm hover:underline mt-2 inline-block">
                Obtener credenciales de Belvo →
              </a>
            </div>
          </div>
        </div>

        <div className="border-t pt-4">
          <p className="text-sm font-medium mb-3">Mientras tanto, puedes:</p>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer" onClick={onClose}>
              <Upload size={20} className="text-gray-500" />
              <div>
                <p className="font-medium">Importar desde Excel/PDF</p>
                <p className="text-sm text-gray-500">Usa los botones de importación</p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </div>
    );
  }

  // Main view - Show connections and option to add new
  return (
    <div className="space-y-4 py-4">
      {/* Existing Connections */}
      {connections.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Conexiones activas</Label>
          {connections.map(conn => (
            <div key={conn.id} className="flex items-center justify-between p-3 border rounded-lg bg-green-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Building2 size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="font-medium">{conn.institution_name}</p>
                  <p className="text-xs text-gray-500">{conn.banco} - {conn.bank_account_name}</p>
                  {conn.last_sync && (
                    <p className="text-xs text-green-600">
                      Última sync: {new Date(conn.last_sync).toLocaleString('es-MX')}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => handleSync(conn.id)}
                  disabled={syncing}
                >
                  <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="text-red-500"
                  onClick={() => handleDeleteConnection(conn.id)}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add new connection */}
      {step === 'status' && (
        <div className="space-y-3">
          <Button 
            className="w-full gap-2" 
            onClick={() => setStep('select')}
          >
            <Plus size={16} />
            Conectar nueva cuenta bancaria
          </Button>
        </div>
      )}

      {/* Step: Select bank */}
      {step === 'select' && (
        <div className="space-y-3">
          <Label>Selecciona tu banco</Label>
          <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
            {institutions.slice(0, 12).map(inst => (
              <div
                key={inst.id}
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedInstitution?.id === inst.id 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'hover:bg-gray-50'
                }`}
                onClick={() => setSelectedInstitution(inst)}
              >
                <div className="flex items-center gap-2">
                  {inst.logo ? (
                    <img src={inst.logo} alt={inst.display_name} className="w-8 h-8 object-contain" />
                  ) : (
                    <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center">
                      <Building2 size={16} />
                    </div>
                  )}
                  <span className="text-sm font-medium truncate">{inst.display_name}</span>
                </div>
              </div>
            ))}
          </div>
          
          {selectedInstitution && (
            <>
              <Label>Cuenta bancaria local</Label>
              <Select value={selectedBankAccount} onValueChange={setSelectedBankAccount}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar cuenta..." />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.map(acc => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.banco} - {acc.nombre} ({acc.moneda})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep('status')}>Cancelar</Button>
            <Button 
              onClick={() => setStep('credentials')} 
              disabled={!selectedInstitution || !selectedBankAccount}
            >
              Continuar
            </Button>
          </div>
        </div>
      )}

      {/* Step: Credentials */}
      {step === 'credentials' && (
        <div className="space-y-3">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>{selectedInstitution?.display_name}</strong> - Ingresa tus credenciales de banca en línea
            </p>
          </div>

          <div className="space-y-2">
            <Label>Usuario / Número de cliente</Label>
            <Input
              value={credentials.username}
              onChange={(e) => setCredentials({...credentials, username: e.target.value})}
              placeholder="Tu usuario de banca en línea"
            />
          </div>

          <div className="space-y-2">
            <Label>Contraseña</Label>
            <Input
              type="password"
              value={credentials.password}
              onChange={(e) => setCredentials({...credentials, password: e.target.value})}
              placeholder="Tu contraseña de banca en línea"
            />
          </div>

          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs text-yellow-700">
              🔒 Tus credenciales se envían de forma segura a través de Belvo y no se almacenan en nuestros servidores.
            </p>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep('select')}>Atrás</Button>
            <Button 
              onClick={handleConnect} 
              disabled={loading || !credentials.username || !credentials.password}
              className="flex-1"
            >
              {loading ? 'Conectando...' : 'Conectar Banco'}
            </Button>
          </div>
        </div>
      )}

      {step === 'status' && (
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      )}
    </div>
  );
};

const BankStatementsModule = () => {
  const [bankTransactions, setBankTransactions] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [cfdis, setCfdis] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [filterAccount, setFilterAccount] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTercero, setSearchTercero] = useState('');  // New: search by tercero (emisor/receptor)
  const [filterEmisor, setFilterEmisor] = useState('all');
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [importFile, setImportFile] = useState(null);
  const [importAccountId, setImportAccountId] = useState('');
  const [importing, setImporting] = useState(false);
  const [duplicatesFound, setDuplicatesFound] = useState([]);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [selectedCfdis, setSelectedCfdis] = useState([]);
  const [cfdiSearchTerm, setCfdiSearchTerm] = useState('');
  const [importPdfDialogOpen, setImportPdfDialogOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfAccountId, setPdfAccountId] = useState('');
  const [importingPdf, setImportingPdf] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // ── AMEX Import state ─────────────────────────────────────────
  const [amexDialogOpen, setAmexDialogOpen] = useState(false);
  const [amexFile, setAmexFile] = useState(null);
  const [importingAmex, setImportingAmex] = useState(false);
  const [amexResult, setAmexResult] = useState(null);
  const [transferFromAccount, setTransferFromAccount] = useState('');
  const [transferToAccount, setTransferToAccount] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  
  // Highlight state for auto-selected CFDI
  const [highlightedCfdiId, setHighlightedCfdiId] = useState(null);
  const cfdiListRef = useRef(null);
  
  // Montos parciales para conciliación - { cfdiId: montoAplicar }
  const [montosParciales, setMontosParciales] = useState({});
  
  // Categorías y subcategorías por CFDI para conciliación - { cfdiId: { categoria_id, subcategoria } }
  const [cfdiCategories, setCfdiCategories] = useState({});
  
  // Historial de pagos del CFDI
  const [paymentHistoryDialogOpen, setPaymentHistoryDialogOpen] = useState(false);
  const [selectedCfdiForHistory, setSelectedCfdiForHistory] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [formData, setFormData] = useState({
    bank_account_id: '',
    fecha_movimiento: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    fecha_valor: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    descripcion: '',
    referencia: '',
    monto: '',
    tipo_movimiento: 'credito',
    saldo: ''
  });
  
  // State for editing transaction
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editFormData, setEditFormData] = useState({
    bank_account_id: '',
    descripcion: '',
    referencia: '',
    monto: '',
    tipo_movimiento: 'credito',
    fecha_movimiento: '',
    notas: ''
  });

  // Exchange rates for conversion
  const [fxRates, setFxRates] = useState({ USD: 17.5, EUR: 19.0 });
  
  // Bank accounts summary with correct FX rates from fecha_saldo
  const [bankSummary, setBankSummary] = useState(null);
  
  // Historical exchange rate for reconciliation (based on transaction date)
  const [historicalFxRate, setHistoricalFxRate] = useState(null);
  
  // Reconciliation summary
  const [reconSummary, setReconSummary] = useState(null);
  
  // Exchange rate for first day of month (for balance calculations)
  const [fxRateFirstOfMonth, setFxRateFirstOfMonth] = useState({ USD: 17.5, EUR: 19.0 });
  
  // Custom exchange rate override for reconciliation (editable by user)
  const [customFxRate, setCustomFxRate] = useState(null);
  const [isEditingFxRate, setIsEditingFxRate] = useState(false);

  useEffect(() => {
    loadData();
    loadFxRates();
    loadReconSummary();
    loadFxRateFirstOfMonth();
  }, []);

  const loadReconSummary = async () => {
    try {
      const res = await api.get('/reconciliations/summary');
      setReconSummary(res.data);
    } catch (error) {
      console.log('Error loading reconciliation summary');
    }
  };
  
  // Load FX rate for first day of the month based on transactions
  // Uses the earliest transaction date to determine the month
  const loadFxRateFirstOfMonth = async (transactions = null) => {
    try {
      const txns = transactions || bankTransactions;
      let year = null;
      let month = null;
      
      // Find the earliest transaction date to determine the month
      if (txns && txns.length > 0) {
        const sortedTxns = [...txns].sort((a, b) => 
          new Date(a.fecha_movimiento) - new Date(b.fecha_movimiento)
        );
        const earliestDate = new Date(sortedTxns[0].fecha_movimiento);
        year = earliestDate.getFullYear();
        month = earliestDate.getMonth() + 1; // getMonth() is 0-indexed
      }
      
      // Build query params
      const params = year && month ? `&year=${year}&month=${month}` : '';
      
      const usdRes = await api.get(`/fx-rates/first-of-month?moneda=USD${params}`);
      const eurRes = await api.get(`/fx-rates/first-of-month?moneda=EUR${params}`);
      setFxRateFirstOfMonth({
        USD: usdRes.data.tasa || 17.5,
        EUR: eurRes.data.tasa || 19.0,
        fecha: usdRes.data.fecha,
        year: usdRes.data.year,
        month: usdRes.data.month
      });
    } catch (error) {
      console.log('Using default FX rates for first of month');
    }
  };

  const loadFxRates = async () => {
    try {
      const res = await api.get('/fx-rates');
      const rates = {};
      res.data.forEach(rate => {
        if (rate.moneda_destino === 'MXN') {
          rates[rate.moneda_origen] = rate.tasa;
        }
      });
      if (rates.USD || rates.EUR) {
        setFxRates(prev => ({ ...prev, ...rates }));
      }
    } catch (error) {
      console.log('Using default FX rates');
    }
  };

  // Get historical exchange rate for a specific date
  const getHistoricalFxRate = async (moneda, fecha) => {
    if (moneda === 'MXN') {
      return { tasa: 1.0, fecha: fecha };
    }
    try {
      const fechaStr = fecha ? (fecha instanceof Date ? fecha.toISOString().split('T')[0] : fecha.split('T')[0]) : null;
      const res = await api.get(`/fx-rates/by-date?moneda=${moneda}&fecha=${fechaStr}`);
      return { tasa: res.data.tasa, fecha: res.data.fecha };
    } catch (error) {
      console.log('Error getting historical FX rate, using default');
      const defaultRates = { USD: 17.5, EUR: 19.0 };
      return { tasa: defaultRates[moneda] || 1.0, fecha: fecha };
    }
  };

  const convertToMXN = (monto, moneda) => {
    if (!monto) return 0;
    if (moneda === 'MXN') return monto;
    if (moneda === 'USD') return monto * (fxRates.USD || 17.5);
    if (moneda === 'EUR') return monto * (fxRates.EUR || 19.0);
    return monto;
  };

  // Convert using historical rate (for reconciliation)
  const convertToMXNHistorical = (monto, moneda, rate) => {
    if (!monto) return 0;
    if (moneda === 'MXN') return monto;
    return monto * (rate || fxRates[moneda] || 17.5);
  };

  const loadData = async () => {
    try {
      const [txnRes, accountsRes, cfdisRes, categoriesRes, summaryRes] = await Promise.all([
        api.get('/bank-transactions?limit=500'),
        api.get('/bank-accounts'),
        api.get('/cfdi?limit=500'),
        api.get('/categories'),
        api.get('/bank-accounts/summary')
      ]);
      setBankTransactions(txnRes.data);
      setBankAccounts(accountsRes.data);
      setCfdis(cfdisRes.data);
      setCategories(categoriesRes.data || []);
      setBankSummary(summaryRes.data);
      
      // Use FX rates from summary (which uses fecha_saldo) 
      if (summaryRes.data?.tipos_cambio) {
        setFxRates(prev => ({ ...prev, ...summaryRes.data.tipos_cambio }));
      }
      
      // Load FX rate based on earliest transaction date
      if (txnRes.data && txnRes.data.length > 0) {
        loadFxRateFirstOfMonth(txnRes.data);
      }
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.bank_account_id) {
      toast.error('Selecciona una cuenta bancaria');
      return;
    }
    try {
      await api.post('/bank-transactions', {
        ...formData,
        monto: parseFloat(formData.monto),
        saldo: parseFloat(formData.saldo) || 0
      });
      toast.success('Movimiento agregado correctamente');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error guardando movimiento');
    }
  };

  const resetForm = () => {
    setFormData({
      bank_account_id: '',
      fecha_movimiento: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      fecha_valor: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      descripcion: '',
      referencia: '',
      monto: '',
      tipo_movimiento: 'credito',
      saldo: ''
    });
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este movimiento?')) return;
    try {
      await api.delete(`/bank-transactions/${id}`);
      toast.success('Movimiento eliminado');
      loadData();
    } catch (error) {
      toast.error('Error eliminando movimiento');
    }
  };

  // Delete all bank transactions
  const handleDeleteAllTransactions = async () => {
    setDeletingAll(true);
    try {
      // Build query params for filtered deletion
      const params = new URLSearchParams();
      if (filterAccount !== 'all') params.append('bank_account_id', filterAccount);
      if (filterStatus !== 'all') params.append('estado_conciliacion', filterStatus);
      
      const queryString = params.toString();
      const url = queryString ? `/bank-transactions/bulk-delete?${queryString}` : '/bank-transactions/bulk-delete';
      
      const response = await api.delete(url);
      toast.success(`Se eliminaron ${response.data.deleted} movimientos correctamente`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error eliminando movimientos');
    } finally {
      setDeleteAllDialogOpen(false);
      setDeletingAll(false);
    }
  };

  // Toggle CFDI selection for multi-reconciliation
  const toggleCfdiSelection = (cfdi) => {
    setSelectedCfdis(prev => {
      const exists = prev.find(c => c.id === cfdi.id);
      if (exists) {
        // Remove CFDI and its partial amount
        setMontosParciales(prevMontos => {
          const newMontos = {...prevMontos};
          delete newMontos[cfdi.id];
          return newMontos;
        });
        return prev.filter(c => c.id !== cfdi.id);
      } else {
        // Add CFDI and initialize with saldo_pendiente or total
        const saldoPendiente = cfdi.saldo_pendiente ?? (cfdi.total - (cfdi.monto_pagado || 0));
        setMontosParciales(prevMontos => ({
          ...prevMontos,
          [cfdi.id]: Math.max(0, saldoPendiente)
        }));
        return [...prev, cfdi];
      }
    });
  };
  
  // Update partial amount for a CFDI
  const updateMontoParcial = (cfdiId, monto) => {
    setMontosParciales(prev => ({
      ...prev,
      [cfdiId]: parseFloat(monto) || 0
    }));
  };
  
  // Get monto to apply for a CFDI (partial or full)
  const getMontoAplicar = (cfdi) => {
    if (montosParciales.hasOwnProperty(cfdi.id)) {
      return montosParciales[cfdi.id];
    }
    // Default to saldo pendiente
    return cfdi.saldo_pendiente ?? (cfdi.total - (cfdi.monto_pagado || 0));
  };
  
  // Load payment history for a CFDI
  const loadPaymentHistory = async (cfdi) => {
    setSelectedCfdiForHistory(cfdi);
    setPaymentHistoryDialogOpen(true);
    setLoadingHistory(true);
    try {
      const response = await api.get(`/cfdi/${cfdi.id}/payment-history`);
      setPaymentHistory(response.data);
    } catch (error) {
      console.error('Error loading payment history:', error);
      toast.error('Error al cargar historial de pagos');
      setPaymentHistory(null);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Calculate totals for reconciliation - handles currency conversion using HISTORICAL rate
  // Can be overridden with customFxRate if user wants to edit it
  // NOW USES PARTIAL AMOUNTS (montosParciales) instead of full CFDI totals
  const getReconciliationTotals = () => {
    const movimientoMonto = selectedTransaction?.monto || 0;
    
    // Get transaction currency from account or transaction itself
    const transactionAccount = bankAccounts.find(a => a.id === selectedTransaction?.bank_account_id);
    const movimientoMoneda = selectedTransaction?.moneda || transactionAccount?.moneda || 'MXN';
    
    // Use custom rate if set, otherwise historical rate, otherwise fallback to current rates
    const tcHistorico = customFxRate ?? historicalFxRate?.tasa ?? fxRates[movimientoMoneda] ?? 17.5;
    
    // Convert movement amount to MXN using HISTORICAL rate (date of transaction)
    const movimientoMontoMXN = convertToMXNHistorical(movimientoMonto, movimientoMoneda, tcHistorico);
    
    // Sum CFDIs - track both original currency and MXN totals
    // USE PARTIAL AMOUNTS (getMontoAplicar) instead of cfdi.total
    let cfdiTotalMXN = 0;
    let cfdiTotalOriginal = 0; // Total in original currency (same as movement)
    
    selectedCfdis.forEach(cfdi => {
      const cfdiMoneda = cfdi.moneda || 'MXN';
      const cfdiMonto = getMontoAplicar(cfdi); // USE PARTIAL AMOUNT!
      
      // Track total in original currency (for USD difference)
      if (cfdiMoneda === movimientoMoneda) {
        cfdiTotalOriginal += cfdiMonto;
        cfdiTotalMXN += convertToMXNHistorical(cfdiMonto, cfdiMoneda, tcHistorico);
      } else if (cfdiMoneda === 'MXN') {
        cfdiTotalMXN += cfdiMonto;
        // Convert MXN to original currency for tracking
        if (movimientoMoneda !== 'MXN' && tcHistorico > 0) {
          cfdiTotalOriginal += cfdiMonto / tcHistorico;
        }
      } else {
        // Different foreign currency - use current rates as approximation
        cfdiTotalMXN += convertToMXN(cfdiMonto, cfdiMoneda);
      }
    });
    
    // Calculate difference in MXN (converted values)
    const diferenciaMXN = movimientoMontoMXN - cfdiTotalMXN;
    
    // Calculate difference in original currency (USD)
    const diferenciaOriginal = movimientoMonto - cfdiTotalOriginal;
    
    // Also return original values for display (now using partial amounts)
    const cfdiTotal = selectedCfdis.reduce((sum, cfdi) => sum + getMontoAplicar(cfdi), 0);
    
    // Get transaction date for display
    const fechaMovimiento = selectedTransaction?.fecha_movimiento || null;
    
    return { 
      movimientoMonto,           // Original amount
      movimientoMoneda,          // Original currency  
      movimientoMontoMXN,        // Converted to MXN using historical rate
      cfdiTotal,                 // Original CFDI total (might be in different currency)
      cfdiTotalMXN,              // CFDI total in MXN
      cfdiTotalOriginal,         // CFDI total in original currency (USD)
      diferencia: diferenciaMXN, // Difference in MXN
      diferenciaMXN,             // Alias for clarity
      diferenciaOriginal,        // Difference in original currency (USD)
      tcUsado: tcHistorico,      // Historical exchange rate used
      fechaTc: historicalFxRate?.fecha || fechaMovimiento,  // Date of the rate
      esHistorico: !!historicalFxRate,  // Flag to indicate if historical rate is being used
      esCustom: customFxRate !== null   // Flag to indicate if custom rate is being used
    };
  };

  // Confirm multi-reconciliation
  const handleConfirmReconciliation = async () => {
    if (!selectedTransaction || selectedCfdis.length === 0) {
      toast.error('Selecciona al menos un CFDI');
      return;
    }
    
    // Check if transaction is already reconciled
    if (selectedTransaction.conciliado) {
      toast.error('Este movimiento ya está conciliado. Cancela la conciliación existente primero.');
      return;
    }
    
    // Validate that all CFDIs have a category selected
    const missingCategories = selectedCfdis.filter(cfdi => !cfdiCategories[cfdi.id]?.categoria_id);
    if (missingCategories.length > 0) {
      toast.error(`Selecciona una categoría para ${missingCategories.length === 1 ? 'el CFDI' : 'los ' + missingCategories.length + ' CFDIs'} pendientes`);
      return;
    }
    
    try {
      let successCount = 0;
      let errorMessages = [];
      
      // Reconcile each selected CFDI with PARTIAL AMOUNT
      for (const cfdi of selectedCfdis) {
        try {
          const montoAplicar = getMontoAplicar(cfdi);
          const categoryData = cfdiCategories[cfdi.id] || {};
          
          await api.post('/reconciliations', {
            bank_transaction_id: selectedTransaction.id,
            cfdi_id: cfdi.id,
            metodo_conciliacion: 'manual',
            porcentaje_match: 100,
            monto_aplicado: montoAplicar, // Send partial amount!
            categoria_id: categoryData.categoria_id,
            subcategoria: categoryData.subcategoria || ''
          });
          successCount++;
        } catch (cfdiError) {
          const errorDetail = cfdiError.response?.data?.detail || 'Error desconocido';
          errorMessages.push(`${cfdi.uuid?.substring(0, 8) || 'CFDI'}: ${errorDetail}`);
          console.error('Error reconciling CFDI:', cfdi.id, cfdiError);
        }
      }
      
      if (successCount === selectedCfdis.length) {
        const { diferenciaMXN } = getReconciliationTotals();
        if (Math.abs(diferenciaMXN) < 0.01) {
          toast.success(`Movimiento conciliado con ${selectedCfdis.length} CFDI(s) - Cuadrado perfectamente`);
        } else {
          toast.success(`Movimiento conciliado con ${selectedCfdis.length} CFDI(s) - Diferencia: $${diferenciaMXN.toFixed(2)} MXN`);
        }
        setReconcileDialogOpen(false);
        setSelectedTransaction(null);
        setSelectedCfdis([]);
        setMontosParciales({}); // Clear partial amounts
        setCfdiCategories({}); // Clear categories
        setCfdiSearchTerm('');
      } else if (successCount > 0) {
        toast.warning(`Parcialmente conciliado: ${successCount}/${selectedCfdis.length} CFDIs. Errores: ${errorMessages.join(', ')}`);
      } else {
        toast.error(`Error al conciliar: ${errorMessages.join(', ')}`);
      }
      
      // Always reload data to refresh state
      loadData();
      
      // Reload CFDIs to update estado_conciliacion
      const cfdisRes = await api.get('/cfdi?limit=500');
      setCfdis(cfdisRes.data);
      
    } catch (error) {
      console.error('Reconciliation error:', error);
      toast.error(error.response?.data?.detail || 'Error al conciliar: ' + (error.message || 'Error desconocido'));
    }
  };

  const handleReconcile = async (cfdiId) => {
    if (!selectedTransaction) return;
    try {
      await api.post('/reconciliations', {
        bank_transaction_id: selectedTransaction.id,
        cfdi_id: cfdiId,
        metodo_conciliacion: 'manual',
        tipo_conciliacion: 'con_uuid',
        porcentaje_match: 100
      });
      toast.success('Movimiento conciliado con CFDI');
      setReconcileDialogOpen(false);
      setSelectedTransaction(null);
      setSelectedCfdis([]);
      loadData();
      loadReconSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al conciliar');
    }
  };

  // Cancel/delete a reconciliation to allow re-reconciliation
  const handleCancelReconciliation = async (txn) => {
    if (!txn.conciliado) return;
    
    // Confirm before canceling
    const confirmCancel = window.confirm(
      `¿Estás seguro de cancelar la conciliación de "${txn.descripcion?.substring(0, 40)}..."?\n\nEsto permitirá volver a conciliar el movimiento con otro CFDI.`
    );
    
    if (!confirmCancel) return;
    
    try {
      toast.loading('Buscando conciliaciones...', { id: 'cancel-recon' });
      
      // Get all reconciliations for this bank transaction
      const res = await api.get('/reconciliations', { params: { limit: 1000 } });
      const reconciliations = res.data.filter(r => r.bank_transaction_id === txn.id);
      
      if (reconciliations.length === 0) {
        // No reconciliation found, just reset the transaction
        toast.dismiss('cancel-recon');
        toast.info('No se encontró registro de conciliación, actualizando estado...');
        // Try to reset manually - this shouldn't normally happen
        await api.put(`/bank-transactions/${txn.id}`, { ...txn, conciliado: false });
        loadData();
        return;
      }
      
      // Delete all reconciliations for this transaction
      for (const recon of reconciliations) {
        await api.delete(`/reconciliations/${recon.id}`);
      }
      
      toast.dismiss('cancel-recon');
      toast.success(`Conciliación cancelada. El movimiento está ahora disponible para re-conciliar.`);
      loadData();
      loadReconSummary();
      
      // Also reload CFDIs to refresh their estado_conciliacion
      const cfdisRes = await api.get('/cfdi?limit=500');
      setCfdis(cfdisRes.data);
      
    } catch (error) {
      toast.dismiss('cancel-recon');
      console.error('Error canceling reconciliation:', error);
      toast.error(error.response?.data?.detail || 'Error al cancelar conciliación');
    }
  };

  // Mark transaction as reconciled WITHOUT UUID - with category selection
  const [sinUUIDDialogOpen, setSinUUIDDialogOpen] = useState(false);
  const [sinUUIDTransaction, setSinUUIDTransaction] = useState(null);
  const [sinUUIDFormData, setSinUUIDFormData] = useState({
    tipo_conciliacion: 'sin_uuid',
    categoria: '',
    concepto: '',
    notas: ''
  });

  // Default expense categories for Sin UUID dialog
  const DEFAULT_EXPENSE_CATEGORIES = [
    { value: 'comision_bancaria', label: 'Comisión Bancaria' },
    { value: 'gasto_sin_factura', label: 'Gasto sin Factura' },
    { value: 'transferencia_interna', label: 'Transferencia Interna' },
    { value: 'pago_nomina', label: 'Pago de Nómina' },
    { value: 'impuestos', label: 'Impuestos / ISR / IVA' },
    { value: 'intereses', label: 'Intereses' },
    { value: 'retiro_efectivo', label: 'Retiro en Efectivo' },
    { value: 'deposito_no_identificado', label: 'Depósito No Identificado' },
    { value: 'otro', label: 'Otro' }
  ];

  // Combined categories: user categories + default categories
  const getAllCategories = () => {
    const tipo = sinUUIDTransaction?.tipo_movimiento === 'credito' ? 'ingreso' : 'egreso';
    const userCats = categories
      .filter(c => c.tipo === tipo && c.activo !== false)
      .map(c => ({ value: `user_${c.id}`, label: c.nombre, isUser: true, categoryId: c.id }));
    return [...userCats, ...DEFAULT_EXPENSE_CATEGORIES];
  };
  
  // Get categories for reconciliation dialog based on CFDI type
  const getCategoriesForCfdi = (cfdi) => {
    // Determine if it's income or expense based on CFDI type
    const tipoCfdi = cfdi?.tipo_cfdi || '';
    const tipo = (tipoCfdi === 'ingreso' || tipoCfdi === 'I') ? 'ingreso' : 'egreso';
    
    const userCats = categories
      .filter(c => c.tipo === tipo && c.activo !== false)
      .map(c => ({ id: c.id, nombre: c.nombre }));
    
    // Add default categories if no user categories exist for this type
    if (userCats.length === 0) {
      return DEFAULT_EXPENSE_CATEGORIES.map(c => ({ id: c.value, nombre: c.label }));
    }
    
    return userCats;
  };
  
  // Get subcategories for a selected category
  const getSubcategoriesForCategory = (categoryId) => {
    if (!categoryId) return [];
    const category = categories.find(c => c.id === categoryId);
    if (!category || !category.subcategorias) return [];
    return category.subcategorias
      .filter(s => s.activo !== false)
      .map(s => ({ id: s.id, nombre: s.nombre }));
  };

  const openSinUUIDDialog = (txn, tipo = 'sin_uuid') => {
    setSinUUIDTransaction(txn);
    setSinUUIDFormData({
      tipo_conciliacion: tipo,
      categoria: '',
      concepto: txn.descripcion || '',
      notas: ''
    });
    setSinUUIDDialogOpen(true);
  };

  const handleMarkWithoutUUID = async () => {
    if (!sinUUIDTransaction) return;
    
    try {
      const res = await api.post('/reconciliations/mark-without-uuid', {
        bank_transaction_id: sinUUIDTransaction.id,
        tipo_conciliacion: sinUUIDFormData.tipo_conciliacion,
        categoria: sinUUIDFormData.categoria,
        concepto: sinUUIDFormData.concepto,
        notas: sinUUIDFormData.notas
      });
      
      const tipoLabel = sinUUIDFormData.tipo_conciliacion === 'sin_uuid' ? 'Sin UUID' : 'No relacionado';
      toast.success(`Movimiento marcado como "${tipoLabel}" y registrado en Cobranza y Pagos`);
      
      setSinUUIDDialogOpen(false);
      setSinUUIDTransaction(null);
      loadData();
      loadReconSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al marcar movimiento');
    }
  };

  // Quick mark without dialog (for backward compatibility)
  const handleQuickMarkWithoutUUID = async (txn, tipo) => {
    try {
      await api.post('/reconciliations/mark-without-uuid', {
        bank_transaction_id: txn.id,
        tipo_conciliacion: tipo,
        categoria: tipo === 'sin_uuid' ? 'gasto_sin_factura' : 'transferencia_interna',
        concepto: txn.descripcion || '',
        notas: tipo === 'sin_uuid' ? 'Conciliado sin UUID - pago sin factura' : 'No relacionado - movimiento interno'
      });
      toast.success(tipo === 'sin_uuid' 
        ? 'Movimiento marcado como "Sin UUID" y registrado como pago' 
        : 'Movimiento marcado como "No relacionado"'
      );
      loadData();
      loadReconSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al marcar movimiento');
    }
  };

  // Edit transaction functions
  const handleEditTransaction = (txn) => {
    setEditingTransaction(txn);
    setEditFormData({
      bank_account_id: txn.bank_account_id || '',
      descripcion: txn.descripcion || '',
      referencia: txn.referencia || '',
      monto: txn.monto?.toString() || '',
      tipo_movimiento: txn.tipo_movimiento || 'credito',
      fecha_movimiento: txn.fecha_movimiento ? format(new Date(txn.fecha_movimiento), "yyyy-MM-dd'T'HH:mm") : '',
      notas: txn.notas || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdateTransaction = async (e) => {
    e.preventDefault();
    if (!editingTransaction) return;
    
    try {
      await api.put(`/bank-transactions/${editingTransaction.id}`, {
        ...editFormData,
        monto: parseFloat(editFormData.monto)
      });
      toast.success('Movimiento actualizado');
      setEditDialogOpen(false);
      setEditingTransaction(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error actualizando movimiento');
    }
  };

  // Delete all reconciliations
  const handleDeleteAllReconciliations = async () => {
    if (!window.confirm('⚠️ ¿Estás SEGURO de que quieres ELIMINAR TODAS las conciliaciones?\n\nTodos los movimientos volverán a estado "Pendiente".\nEsta acción NO se puede deshacer.')) {
      return;
    }
    if (!window.confirm('🚨 ÚLTIMA CONFIRMACIÓN: Se borrarán TODAS las conciliaciones.\n\n¿Continuar?')) {
      return;
    }
    
    try {
      const res = await api.delete('/reconciliations/bulk/all');
      toast.success(res.data.message);
      loadData();
      loadReconSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error eliminando conciliaciones');
    }
  };

  // ── Importar CSV de AMEX ──────────────────────────────────────
  const handleAmexFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Solo se aceptan archivos CSV descargados de AMEX');
      e.target.value = '';
      return;
    }
    setAmexFile(file);
    setAmexResult(null);
    setAmexDialogOpen(true);
    e.target.value = '';
  };

  const handleAmexImport = async () => {
    if (!amexFile) return;
    setImportingAmex(true);
    try {
      const formData = new FormData();
      formData.append('file', amexFile);
      formData.append('cuenta_nombre', 'AMEX');
      const res = await api.post('/amex/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAmexResult(res.data);
      if (res.data.insertados > 0) {
        toast.success(`✅ AMEX: ${res.data.insertados} movimientos importados`);
        loadData();
      } else {
        toast.info('Sin movimientos nuevos — todos ya estaban importados');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al importar AMEX');
    } finally {
      setImportingAmex(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.get('/bank-transactions/template', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_estado_cuenta.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Plantilla descargada');
    } catch (error) {
      toast.error('Error descargando plantilla');
    }
  };

  const exportToExcel = () => {
    if (filteredTransactions.length === 0) {
      toast.error('No hay movimientos para exportar');
      return;
    }

    const data = filteredTransactions.map(txn => {
      const account = bankAccounts.find(a => a.id === txn.bank_account_id);
      return {
        'Fecha': format(new Date(txn.fecha_movimiento), 'dd/MM/yyyy'),
        'Cuenta': account?.nombre || 'N/A',
        'Banco': account?.banco || 'N/A',
        'Descripción': txn.descripcion,
        'Referencia': txn.referencia || '',
        'Tipo': txn.tipo_movimiento === 'credito' ? 'Depósito' : 'Retiro',
        'Monto': txn.monto,
        'Saldo': txn.saldo,
        'Moneda': txn.moneda || account?.moneda || 'MXN',
        'Conciliado': txn.conciliado ? 'Sí' : 'No'
      };
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Movimientos');
    XLSX.writeFile(wb, `Estados_Cuenta_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    toast.success('Exportado a Excel');
  };

  // Handle PDF file selection
  const handlePdfSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (bankAccounts.length === 0) {
      toast.error('Primero crea una cuenta bancaria');
      e.target.value = '';
      return;
    }
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Solo se aceptan archivos PDF');
      e.target.value = '';
      return;
    }
    
    setPdfFile(file);
    setPdfAccountId(bankAccounts[0]?.id || '');
    setPdfPreview(null);
    setImportPdfDialogOpen(true);
    e.target.value = '';
    
    // Auto-load preview
    loadPdfPreview(file);
  };

  // Load PDF preview
  const loadPdfPreview = async (file) => {
    setLoadingPreview(true);
    setPdfPreview(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('banco', 'auto');

      const response = await api.post('/bank-transactions/preview-pdf', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setPdfPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error leyendo PDF');
      setPdfPreview({ status: 'error', message: 'No se pudo leer el PDF' });
    } finally {
      setLoadingPreview(false);
    }
  };

  // Process PDF import
  const processPdfImport = async () => {
    if (!pdfFile || !pdfAccountId) {
      toast.error('Selecciona una cuenta bancaria');
      return;
    }

    setImportingPdf(true);

    try {
      const formData = new FormData();
      formData.append('file', pdfFile);
      formData.append('bank_account_id', pdfAccountId);
      formData.append('banco', 'auto');

      const response = await api.post('/bank-transactions/import-pdf', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const result = response.data;

      if (result.importados > 0) {
        toast.success(`${result.importados} movimientos importados del PDF`);
      }
      
      if (result.duplicados_omitidos > 0) {
        toast.warning(`${result.duplicados_omitidos} duplicados omitidos`);
      }
      
      if (result.importados === 0 && result.status === 'warning') {
        toast.info(result.message);
      }

      setImportPdfDialogOpen(false);
      setPdfFile(null);
      setPdfAccountId('');
      setPdfPreview(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error importando PDF');
    } finally {
      setImportingPdf(false);
    }
  };

  // Handle file selection - opens dialog to select account
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (bankAccounts.length === 0) {
      toast.error('Primero crea una cuenta bancaria');
      e.target.value = '';
      return;
    }
    
    setImportFile(file);
    setImportAccountId(bankAccounts[0]?.id || '');
    setImportDialogOpen(true);
    e.target.value = '';
  };

  // Process the import after account is selected
  const processImport = async () => {
    if (!importFile || !importAccountId) {
      toast.error('Selecciona una cuenta bancaria');
      return;
    }

    setImporting(true);

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          toast.error('El archivo está vacío');
          setImporting(false);
          return;
        }

        let imported = 0;
        let errors = 0;
        let duplicates = 0;

        console.log('Starting import with account:', importAccountId);
        console.log('Total rows to process:', jsonData.length);

        for (const row of jsonData) {
          try {
            console.log('Processing row:', row);
            
            // Parse date - handle multiple formats
            let fechaMovimiento = new Date();
            const fechaRaw = row['fecha_movimiento'] || row['Fecha'] || row['FECHA'] || row['fecha'];
            if (fechaRaw) {
              if (fechaRaw instanceof Date) {
                fechaMovimiento = fechaRaw;
              } else if (typeof fechaRaw === 'string') {
                // Try parsing different formats
                const parsed = new Date(fechaRaw);
                if (!isNaN(parsed.getTime())) {
                  fechaMovimiento = parsed;
                } else {
                  // Try DD/MM/YYYY format
                  const parts = fechaRaw.split('/');
                  if (parts.length === 3) {
                    fechaMovimiento = new Date(parts[2], parts[1] - 1, parts[0]);
                  }
                }
              } else if (typeof fechaRaw === 'number') {
                // Excel serial date number
                const excelEpoch = new Date(1899, 11, 30);
                fechaMovimiento = new Date(excelEpoch.getTime() + fechaRaw * 86400000);
              }
            }
            
            console.log('Parsed date:', fechaMovimiento);

            // Parse amount - the sign of monto determines the type
            let monto = 0;
            let tipoMovimiento = 'credito'; // Default: deposit
            
            // Read the monto column
            if (row['monto'] !== undefined || row['Monto'] !== undefined || row['MONTO'] !== undefined) {
              monto = parseFloat(row['monto'] || row['Monto'] || row['MONTO'] || 0);
            }
            
            // Handle Cargo/Abono columns (alternative format)
            if (monto === 0) {
              if (row['Cargo'] !== undefined || row['CARGO'] !== undefined) {
                monto = -parseFloat(row['Cargo'] || row['CARGO'] || 0); // Cargo is negative
              }
              if (row['Abono'] !== undefined || row['ABONO'] !== undefined) {
                monto = parseFloat(row['Abono'] || row['ABONO'] || 0); // Abono is positive
              }
              if (row['Deposito'] || row['Depósito'] || row['DEPOSITO']) {
                monto = parseFloat(row['Deposito'] || row['Depósito'] || row['DEPOSITO'] || 0);
              }
              if (row['Retiro'] || row['RETIRO']) {
                monto = -parseFloat(row['Retiro'] || row['RETIRO'] || 0);
              }
            }
            
            // Determine tipo based on sign of monto
            // Negative monto = money going out (retiro/cargo) = debito
            // Positive monto = money coming in (deposito/abono) = credito
            if (monto < 0) {
              tipoMovimiento = 'debito'; // Retiro/Cargo
              monto = Math.abs(monto);
            } else {
              tipoMovimiento = 'credito'; // Depósito/Abono
            }
            
            console.log('Monto:', monto, 'Tipo:', tipoMovimiento);

            // Get description
            const descripcion = row['descripcion'] || row['Descripción'] || row['DESCRIPCION'] || 
                               row['Concepto'] || row['CONCEPTO'] || row['concepto'] || 
                               row['Movimiento'] || 'Movimiento importado';

            // Get reference
            const referencia = row['referencia'] || row['Referencia'] || row['REFERENCIA'] || 
                              row['Ref'] || row['REF'] || '';

            // Get saldo
            const saldo = parseFloat(row['saldo'] || row['Saldo'] || row['SALDO'] || 0);

            // Skip rows with 0 amount
            if (monto === 0 || isNaN(monto)) {
              console.log('Skipping row with 0 or NaN monto');
              continue;
            }

            // Get account currency
            const importAccount = bankAccounts.find(a => a.id === importAccountId);
            const accountCurrency = importAccount?.moneda || 'MXN';

            const txnData = {
              bank_account_id: importAccountId,
              fecha_movimiento: fechaMovimiento.toISOString(),
              fecha_valor: fechaMovimiento.toISOString(),
              descripcion: String(descripcion).substring(0, 500),
              referencia: String(referencia).substring(0, 100),
              monto: monto,
              moneda: accountCurrency,  // Always use account's currency
              tipo_movimiento: tipoMovimiento,
              saldo: saldo || 0,
              fuente: 'excel_import'
            };

            // Check for duplicate
            const isDuplicate = bankTransactions.some(existing => 
              existing.descripcion === txnData.descripcion &&
              Math.abs(existing.monto - monto) < 0.01 &&
              existing.fecha_movimiento?.slice(0, 10) === fechaMovimiento.toISOString().slice(0, 10)
            );

            if (isDuplicate) {
              duplicates++;
              console.log('Duplicate found, skipping:', txnData.descripcion);
              continue;
            }

            console.log('Sending transaction:', txnData);
            const response = await api.post('/bank-transactions', txnData);
            console.log('Transaction created:', response.data);
            imported++;
          } catch (err) {
            console.error('Error importing row:', row, 'Error:', err.response?.data || err.message);
            errors++;
          }
        }

        if (imported > 0) {
          toast.success(`${imported} movimientos importados correctamente`);
        }
        if (duplicates > 0) {
          toast.warning(`${duplicates} movimientos duplicados omitidos`);
        }
        if (errors > 0) {
          toast.error(`${errors} filas con errores`);
        }
        if (imported === 0 && errors === 0 && duplicates === 0) {
          toast.info('No se encontraron movimientos válidos para importar');
        }
        
        setImportDialogOpen(false);
        setImportFile(null);
        setImportAccountId('');
        setDuplicatesFound([]);
        setImporting(false);
        loadData();
      };
      reader.readAsArrayBuffer(importFile);
    } catch (error) {
      toast.error('Error importando archivo');
      setImporting(false);
    }
  };

  // Transfer state with currency conversion support
  const [transferConvertCurrency, setTransferConvertCurrency] = useState(true);
  const [transferCustomFxRate, setTransferCustomFxRate] = useState('');
  const [transferSelectedTxns, setTransferSelectedTxns] = useState([]);

  // Transfer transactions between accounts with currency conversion
  const handleTransferTransactions = async () => {
    if (!transferFromAccount || !transferToAccount) {
      toast.error('Selecciona cuenta origen y destino');
      return;
    }
    if (transferFromAccount === transferToAccount) {
      toast.error('La cuenta origen y destino no pueden ser la misma');
      return;
    }

    setTransferring(true);
    try {
      const payload = {
        from_account_id: transferFromAccount,
        to_account_id: transferToAccount,
        convert_currency: transferConvertCurrency
      };
      
      // Include custom FX rate if provided
      if (transferCustomFxRate && parseFloat(transferCustomFxRate) > 0) {
        payload.custom_fx_rate = parseFloat(transferCustomFxRate);
      }
      
      // Include specific transaction IDs if selected
      if (transferSelectedTxns.length > 0) {
        payload.transaction_ids = transferSelectedTxns;
      }
      
      const response = await api.post('/bank-transactions/transfer-account', payload);
      
      const msg = response.data.currency_converted 
        ? `${response.data.message} (TC: ${response.data.fx_rate_used?.toFixed(4)})`
        : response.data.message;
      
      toast.success(msg);
      setTransferDialogOpen(false);
      setTransferFromAccount('');
      setTransferToAccount('');
      setTransferConvertCurrency(true);
      setTransferCustomFxRate('');
      setTransferSelectedTxns([]);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error transfiriendo movimientos');
    } finally {
      setTransferring(false);
    }
  };

  // Get currency info for transfer dialog
  const getAccountCurrency = (accountId) => {
    const account = bankAccounts.find(a => a.id === accountId);
    return account?.moneda || 'MXN';
  };

  const transferFromCurrency = transferFromAccount ? getAccountCurrency(transferFromAccount) : '';
  const transferToCurrency = transferToAccount ? getAccountCurrency(transferToAccount) : '';
  const needsCurrencyConversion = transferFromCurrency && transferToCurrency && transferFromCurrency !== transferToCurrency;

  // Filter transactions
  const filteredTransactions = bankTransactions.filter(txn => {
    if (filterAccount !== 'all' && txn.bank_account_id !== filterAccount) return false;
    if (filterStatus === 'conciliado' && !txn.conciliado) return false;
    if (filterStatus === 'pendiente' && txn.conciliado) return false;
    if (searchTerm && !txn.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !txn.referencia?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    // Filter by Tercero (search in description, and associated CFDI emisor/receptor)
    if (searchTercero) {
      const searchLower = searchTercero.toLowerCase();
      const matchDesc = txn.descripcion?.toLowerCase().includes(searchLower);
      const matchRef = txn.referencia?.toLowerCase().includes(searchLower);
      // Also search in associated CFDI data if available
      const matchCfdiEmisor = txn.cfdi_emisor?.toLowerCase().includes(searchLower);
      const matchCfdiReceptor = txn.cfdi_receptor?.toLowerCase().includes(searchLower);
      const matchBeneficiario = txn.beneficiario?.toLowerCase().includes(searchLower);
      
      if (!matchDesc && !matchRef && !matchCfdiEmisor && !matchCfdiReceptor && !matchBeneficiario) {
        return false;
      }
    }
    
    // Filter by Emisor (from description - typically contains the counterparty name)
    if (filterEmisor !== 'all') {
      const emisorNormalizado = filterEmisor.toLowerCase();
      if (!txn.descripcion?.toLowerCase().includes(emisorNormalizado)) return false;
    }
    // Filter by Categoria
    if (filterCategoria !== 'all' && txn.categoria !== filterCategoria) return false;
    return true;
  });
  
  // Get unique Emisores from transaction descriptions (for dropdown)
  const uniqueEmisores = [...new Set(
    bankTransactions
      .map(txn => {
        // Extract counterparty name from description (before common prefixes like "TRANSFERENCIA", "PAGO", etc.)
        const desc = txn.descripcion || '';
        // Try to extract the main name - take first meaningful part
        const parts = desc.split(/[,-]/).map(p => p.trim());
        return parts[0]?.substring(0, 30) || desc.substring(0, 30);
      })
      .filter(e => e && e.length > 3)
  )].sort();
  
  // Get unique Categorias from transactions
  const uniqueCategorias = [...new Set(
    bankTransactions
      .map(txn => txn.categoria)
      .filter(c => c && c.length > 0)
  )].sort();

  // tipo_movimiento (credito/debito) es el campo estándar; source='alegra' guarda tipo (deposito/retiro)
  const isDeposito = (t) => t.tipo_movimiento === 'credito' || t.tipo === 'deposito' || t.tipo === 'ingreso';

  // Stats
  const totalDepositos = filteredTransactions
    .filter(t => isDeposito(t))
    .reduce((sum, t) => sum + t.monto, 0);
  const totalRetiros = filteredTransactions
    .filter(t => !isDeposito(t))
    .reduce((sum, t) => sum + t.monto, 0);
  const pendientesConciliar = filteredTransactions.filter(t => !t.conciliado).length;
  
  // Get selected account's initial balance and currency
  // When "all" is selected, calculate consolidated balance in MXN
  const selectedAccount = filterAccount !== 'all' 
    ? bankAccounts.find(a => a.id === filterAccount)
    : null;
  
  // Convert to MXN using first-of-month rate (for balance calculations)
  const convertToMXNFirstOfMonth = (monto, moneda) => {
    if (!monto) return 0;
    if (moneda === 'MXN') return monto;
    if (moneda === 'USD') return monto * (fxRateFirstOfMonth.USD || 17.5);
    if (moneda === 'EUR') return monto * (fxRateFirstOfMonth.EUR || 19.0);
    return monto;
  };
  
  // Calculate consolidated initial balance when viewing all accounts
  // Uses the bank summary's total_mxn which already has correct FX rates from fecha_saldo
  const calcConsolidatedSaldoInicial = () => {
    if (filterAccount !== 'all') {
      return selectedAccount?.saldo_inicial || 0;
    }
    // Use the pre-calculated total from bank summary (has correct FX from fecha_saldo)
    if (bankSummary?.total_mxn) {
      return bankSummary.total_mxn;
    }
    // Fallback: Sum all accounts converted to MXN
    return bankAccounts.reduce((total, acc) => {
      const saldo = acc.saldo_inicial || 0;
      const moneda = acc.moneda || 'MXN';
      return total + convertToMXNFirstOfMonth(saldo, moneda);
    }, 0);
  };
  
  const saldoInicial = calcConsolidatedSaldoInicial();
  const monedaCuenta = filterAccount === 'all' ? 'MXN' : (selectedAccount?.moneda || 'MXN');
  
  // For consolidated view, convert all transactions to MXN
  // For individual account, show in account's currency AND equivalent in MXN
  const calcConsolidatedTotals = () => {
    if (filterAccount !== 'all') {
      return { 
        depositos: totalDepositos, 
        retiros: totalRetiros,
        depositosMXN: convertToMXN(totalDepositos, monedaCuenta),
        retirosMXN: convertToMXN(totalRetiros, monedaCuenta)
      };
    }
    // Convert each transaction based on its account's currency
    let depositosMXN = 0;
    let retirosMXN = 0;
    filteredTransactions.forEach(t => {
      const acc = bankAccounts.find(a => a.id === t.bank_account_id);
      const moneda = t.moneda || acc?.moneda || 'MXN';
      const montoMXN = convertToMXN(t.monto, moneda);
      if (t.tipo_movimiento === 'credito') {
        depositosMXN += montoMXN;
      } else {
        retirosMXN += montoMXN;
      }
    });
    return { depositos: depositosMXN, retiros: retirosMXN, depositosMXN, retirosMXN };
  };
  
  const consolidatedTotals = calcConsolidatedTotals();
  const displayDepositos = filterAccount === 'all' ? consolidatedTotals.depositos : totalDepositos;
  const displayRetiros = filterAccount === 'all' ? consolidatedTotals.retiros : totalRetiros;
  const displayDepositosMXN = consolidatedTotals.depositosMXN || convertToMXN(displayDepositos, monedaCuenta);
  const displayRetirosMXN = consolidatedTotals.retirosMXN || convertToMXN(displayRetiros, monedaCuenta);
  const saldoFinal = saldoInicial + displayDepositos - displayRetiros;
  const saldoFinalMXN = convertToMXN(saldoInicial, monedaCuenta) + displayDepositosMXN - displayRetirosMXN;

  if (loading) return <div className="p-8">Cargando...</div>;

  return (
    <div className="p-8 space-y-6" data-testid="bank-statements-page">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="w-full">
          <PageHeader title="Conciliaciones Bancarias" subtitle="Gestión de movimientos bancarios y conciliación con CFDIs" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setConnectDialogOpen(true)} className="gap-2" data-testid="connect-bank-btn">
            <Link2 size={16} />
            Conectar Banco
          </Button>
          <Button variant="outline" onClick={downloadTemplate} className="gap-2" data-testid="download-template-btn">
            <FileSpreadsheet size={16} />
            Descargar Plantilla
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileSelect} className="hidden" />
            <Button variant="outline" className="gap-2" asChild>
              <span>
                <Upload size={16} />
                Importar Excel
              </span>
            </Button>
          </label>
          <label className="cursor-pointer">
            <input type="file" accept=".pdf" onChange={handlePdfSelect} className="hidden" />
            <Button variant="outline" className="gap-2 border-red-200 text-red-700 hover:bg-red-50" asChild data-testid="import-pdf-btn">
              <span>
                <FileText size={16} />
                Importar PDF
              </span>
            </Button>
          </label>

          {/* Botón AMEX */}
          <label className="cursor-pointer">
            <input type="file" accept=".csv" onChange={handleAmexFileSelect} className="hidden" />
            <Button variant="outline" className="gap-1 border-[#007BC1] text-[#007BC1] hover:bg-blue-50 font-semibold" asChild>
              <span className="flex items-center gap-1.5">
                {/* Logo AMEX simplificado */}
                <svg width="22" height="14" viewBox="0 0 22 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="22" height="14" rx="2" fill="#007BC1"/>
                  <text x="2" y="10" fontFamily="Arial" fontWeight="bold" fontSize="7" fill="white">AMEX</text>
                </svg>
                Importar AMEX
              </span>
            </Button>
          </label>
          
          <Button variant="outline" onClick={exportToExcel} className="gap-2" data-testid="export-statements-btn">
            <Download size={16} />
            Exportar
          </Button>
          <Button 
            variant="outline" 
            onClick={handleDeleteAllReconciliations} 
            className="gap-2 text-orange-600 border-orange-300 hover:bg-orange-50" 
            data-testid="delete-all-reconciliations-btn"
          >
            <RotateCcw size={16} />
            Borrar Conciliaciones
          </Button>
          <Button 
            variant="destructive" 
            onClick={() => setDeleteAllDialogOpen(true)} 
            className="gap-2" 
            disabled={filteredTransactions.length === 0}
            data-testid="delete-all-transactions-btn"
          >
            <Trash2 size={16} />
            Eliminar {filteredTransactions.length === bankTransactions.length ? 'Todos' : `(${filteredTransactions.length})`}
          </Button>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-[#0F172A]" data-testid="add-movement-btn">
                <Plus size={16} />
                Agregar Movimiento
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Nuevo Movimiento Bancario</DialogTitle>
                <DialogDescription>Captura un movimiento del estado de cuenta</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Cuenta Bancaria *</Label>
                  <Select value={formData.bank_account_id} onValueChange={(v) => setFormData({...formData, bank_account_id: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar cuenta..." />
                    </SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map(acc => (
                        <SelectItem key={acc.id} value={acc.id}>
                          <span className="flex items-center gap-2">
                            <Building2 size={14} />
                            {acc.banco} - {acc.nombre} ({acc.moneda})
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fecha Movimiento *</Label>
                    <Input 
                      type="datetime-local" 
                      value={formData.fecha_movimiento}
                      onChange={(e) => setFormData({...formData, fecha_movimiento: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha Valor</Label>
                    <Input 
                      type="datetime-local" 
                      value={formData.fecha_valor}
                      onChange={(e) => setFormData({...formData, fecha_valor: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Descripción / Concepto *</Label>
                  <Input 
                    value={formData.descripcion}
                    onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                    placeholder="Ej: Transferencia SPEI, Pago de nómina..."
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Referencia / Folio</Label>
                  <Input 
                    value={formData.referencia}
                    onChange={(e) => setFormData({...formData, referencia: e.target.value})}
                    placeholder="Número de referencia bancaria"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Movimiento *</Label>
                    <Select value={formData.tipo_movimiento} onValueChange={(v) => setFormData({...formData, tipo_movimiento: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="credito">
                          <span className="flex items-center gap-2 text-green-600">
                            <ArrowUpCircle size={14} /> Depósito / Abono
                          </span>
                        </SelectItem>
                        <SelectItem value="debito">
                          <span className="flex items-center gap-2 text-red-600">
                            <ArrowDownCircle size={14} /> Retiro / Cargo
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Monto *</Label>
                    <Input 
                      type="number" 
                      step="0.01"
                      value={formData.monto}
                      onChange={(e) => setFormData({...formData, monto: e.target.value})}
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Saldo después del movimiento</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={formData.saldo}
                    onChange={(e) => setFormData({...formData, saldo: e.target.value})}
                    placeholder="Saldo en el estado de cuenta"
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-[#0F172A]">Guardar Movimiento</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards - Row 1: Flujo */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-gray-200 bg-gray-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Building2 size={16} />
              Saldo Inicial {filterAccount === 'all' && '(Consolidado)'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-gray-700">
              ${saldoInicial.toLocaleString('es-MX', {minimumFractionDigits: 2})}
              <span className="text-xs ml-1">{monedaCuenta}</span>
            </div>
            {filterAccount === 'all' && bankAccounts.length > 0 && (
              <p className="text-xs text-gray-500">{bankAccounts.length} cuenta(s)</p>
            )}
            {filterAccount !== 'all' && monedaCuenta !== 'MXN' && (
              <p className="text-xs text-gray-500 mt-1">
                ≈ ${convertToMXN(saldoInicial, monedaCuenta).toLocaleString('es-MX', {minimumFractionDigits: 2})} MXN
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-700 flex items-center gap-2">
              <ArrowUpCircle size={16} />
              + Depósitos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-green-700">
              ${displayDepositos.toLocaleString('es-MX', {minimumFractionDigits: 2})}
              <span className="text-xs ml-1">{monedaCuenta}</span>
            </div>
            {filterAccount !== 'all' && monedaCuenta !== 'MXN' && (
              <p className="text-xs text-green-600 mt-1">
                ≈ ${convertToMXN(displayDepositos, monedaCuenta).toLocaleString('es-MX', {minimumFractionDigits: 2})} MXN
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-red-700 flex items-center gap-2">
              <ArrowDownCircle size={16} />
              - Retiros
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-red-700">
              ${displayRetiros.toLocaleString('es-MX', {minimumFractionDigits: 2})}
              <span className="text-xs ml-1">{monedaCuenta}</span>
            </div>
            {filterAccount !== 'all' && monedaCuenta !== 'MXN' && (
              <p className="text-xs text-red-600 mt-1">
                ≈ ${convertToMXN(displayRetiros, monedaCuenta).toLocaleString('es-MX', {minimumFractionDigits: 2})} MXN
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-blue-700 flex items-center gap-2">
              <DollarSign size={16} />
              = Saldo Final {filterAccount === 'all' && '(Consolidado)'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-xl font-bold ${saldoFinal >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
              ${saldoFinal.toLocaleString('es-MX', {minimumFractionDigits: 2})}
              <span className="text-xs ml-1">{monedaCuenta}</span>
            </div>
            {filterAccount !== 'all' && monedaCuenta !== 'MXN' && (
              <p className={`text-xs mt-1 ${saldoFinal >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                ≈ ${convertToMXN(saldoFinal, monedaCuenta).toLocaleString('es-MX', {minimumFractionDigits: 2})} MXN
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-yellow-700 flex items-center gap-2">
              <Clock size={16} />
              Pendientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-yellow-700">{pendientesConciliar}</div>
            <p className="text-xs text-yellow-600">sin conciliar</p>
          </CardContent>
        </Card>
      </div>

      {/* Reconciliation Summary Cards */}
      {reconSummary && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="border-purple-200 bg-purple-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-purple-700 flex items-center gap-2">
                <CheckCircle size={16} />
                Con UUID
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-purple-700">{reconSummary.summary?.conciliados_con_uuid || 0}</div>
              <p className="text-xs text-purple-600">
                ${(reconSummary.summary?.monto_con_uuid || 0).toLocaleString('es-MX', {minimumFractionDigits: 2})}
              </p>
            </CardContent>
          </Card>

          <Card className="border-orange-200 bg-orange-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-orange-700 flex items-center gap-2">
                <AlertCircle size={16} />
                Sin UUID
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-orange-700">{reconSummary.summary?.conciliados_sin_uuid || 0}</div>
              <p className="text-xs text-orange-600">
                ${(reconSummary.summary?.monto_sin_uuid || 0).toLocaleString('es-MX', {minimumFractionDigits: 2})}
              </p>
            </CardContent>
          </Card>

          <Card className="border-gray-200 bg-gray-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <X size={16} />
                No Relacionado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-gray-700">{reconSummary.summary?.no_relacionados || 0}</div>
              <p className="text-xs text-gray-600">
                ${(reconSummary.summary?.monto_no_relacionado || 0).toLocaleString('es-MX', {minimumFractionDigits: 2})}
              </p>
            </CardContent>
          </Card>

          <Card className="border-red-200 bg-red-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-red-700 flex items-center gap-2">
                <Clock size={16} />
                Diferencia Pendiente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-red-700">{reconSummary.summary?.pendientes || 0}</div>
              <p className="text-xs text-red-600">
                ${(reconSummary.summary?.monto_pendiente || 0).toLocaleString('es-MX', {minimumFractionDigits: 2})}
              </p>
            </CardContent>
          </Card>

          <Card className="border-green-300 bg-green-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-800 flex items-center gap-2">
                <CheckCircle size={16} />
                % Conciliado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-green-800">{reconSummary.summary?.porcentaje_conciliado || 0}%</div>
              <p className="text-xs text-green-700">
                {(reconSummary.summary?.total_movimientos || 0) - (reconSummary.summary?.pendientes || 0)} de {reconSummary.summary?.total_movimientos || 0}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-gray-500">Buscar</Label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Buscar por descripción o referencia..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-gray-500">Buscar Tercero (Emisor/Receptor)</Label>
              <div className="relative">
                <Building2 size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Nombre de proveedor o cliente..."
                  value={searchTercero}
                  onChange={(e) => setSearchTercero(e.target.value)}
                  className="pl-10"
                  data-testid="search-tercero"
                />
              </div>
            </div>
            <div className="w-48">
              <Label className="text-xs text-gray-500">Cuenta</Label>
              <Select value={filterAccount} onValueChange={setFilterAccount}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las cuentas</SelectItem>
                  {bankAccounts.map(acc => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.banco} - {acc.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label className="text-xs text-gray-500">Estado</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pendiente">Pendientes</SelectItem>
                  <SelectItem value="conciliado">Conciliados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label className="text-xs text-gray-500">Emisor/Cliente</Label>
              <Select value={filterEmisor} onValueChange={setFilterEmisor}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {uniqueEmisores.slice(0, 50).map(emisor => (
                    <SelectItem key={emisor} value={emisor}>
                      {emisor.length > 25 ? emisor.substring(0, 25) + '...' : emisor}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label className="text-xs text-gray-500">Categoría</Label>
              <Select value={filterCategoria} onValueChange={setFilterCategoria}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {uniqueCategorias.map(cat => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(searchTerm || searchTercero || filterAccount !== 'all' || filterStatus !== 'all' || filterEmisor !== 'all' || filterCategoria !== 'all') && (
              <Button variant="ghost" size="sm" onClick={() => {
                setSearchTerm('');
                setSearchTercero('');
                setFilterAccount('all');
                setFilterStatus('all');
                setFilterEmisor('all');
                setFilterCategoria('all');
              }}>
                <X size={16} className="mr-1" /> Limpiar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Movimientos Bancarios</CardTitle>
          <CardDescription>
            {filteredTransactions.length} movimiento{filteredTransactions.length !== 1 ? 's' : ''} encontrado{filteredTransactions.length !== 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {bankAccounts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Building2 size={48} className="mx-auto mb-4 opacity-50" />
              <p className="font-medium">No hay cuentas bancarias</p>
              <p className="text-sm mt-2">Primero crea una cuenta bancaria en el módulo Bancario</p>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileSpreadsheet size={48} className="mx-auto mb-4 opacity-50" />
              <p className="font-medium">No hay movimientos</p>
              <p className="text-sm mt-2">Agrega movimientos manualmente, importa desde Excel o conecta tu banco</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Cuenta</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Referencia</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead className="text-center">Estado</TableHead>
                    <TableHead className="text-center">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.map((txn) => {
                    const account = bankAccounts.find(a => a.id === txn.bank_account_id);
                    // Alegra bank_transactions use cuenta_bancaria (string) instead of bank_account_id
                    const displayBanco = account?.banco || txn.cuenta_bancaria || 'N/A';
                    const displayNombre = account?.nombre || txn.cuenta_bancaria || '-';
                    return (
                      <TableRow key={txn.id} className={txn.conciliado ? 'bg-green-50/50' : ''}>
                        <TableCell className="font-mono text-sm">
                          {format(new Date(txn.fecha_movimiento), 'dd/MM/yyyy', { locale: es })}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-xs font-medium bg-blue-100 text-blue-800 px-2 py-0.5 rounded w-fit">{displayBanco}</span>
                            <span className="text-xs text-gray-500 mt-0.5">{displayNombre}</span>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[250px]">
                          <span className="truncate block">{txn.descripcion}</span>
                          {txn.conciliado && txn.tercero && (
                            <span className="text-xs text-green-700 font-medium truncate block mt-0.5">
                              👤 {txn.tercero}
                            </span>
                          )}
                          {txn.conciliado && txn.cfdi_uuid && (
                            <span className="text-xs text-gray-400 font-mono truncate block">
                              {txn.cfdi_uuid.substring(0, 8)}...
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-gray-500">{txn.referencia || '-'}</TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded font-medium ${
                            isDeposito(txn)
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {isDeposito(txn) ? (
                              <><ArrowUpCircle size={12} /> Depósito</>
                            ) : (
                              <><ArrowDownCircle size={12} /> Retiro</>
                            )}
                          </span>
                        </TableCell>
                        <TableCell className={`text-right font-mono font-semibold ${
                          isDeposito(txn) ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {(() => {
                            const montoDisplay = (txn.moneda_original === 'USD' && txn.monto_original)
                              ? txn.monto_original
                              : txn.monto;
                            const monedaDisplay = txn.moneda_original || txn.moneda || account?.moneda || 'MXN';
                            return <>
                              <div>
                                {isDeposito(txn) ? '+' : '-'}
                                ${Math.abs(montoDisplay).toLocaleString('es-MX', {minimumFractionDigits: 2})}
                                <span className="text-xs ml-1">{monedaDisplay}</span>
                              </div>
                              {monedaDisplay !== 'MXN' && (
                                <div className="text-xs text-gray-400 font-normal">
                                  ≈ ${(txn.moneda_original === 'USD' && txn.monto_original
                                    ? txn.monto
                                    : convertToMXN(txn.monto, txn.moneda || account?.moneda)
                                  ).toLocaleString('es-MX', {minimumFractionDigits: 2})} MXN
                                </div>
                              )}
                            </>;
                          })()}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          ${(txn.saldo || 0).toLocaleString('es-MX', {minimumFractionDigits: 2})}
                          {(txn.moneda || account?.moneda) !== 'MXN' && (
                            <span className="text-xs ml-1 text-gray-400">{txn.moneda || account?.moneda}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {txn.conciliado ? (
                            txn.tipo_conciliacion === 'sin_uuid' ? (
                              <span className="inline-flex items-center gap-1 text-orange-600 text-xs px-2 py-1 bg-orange-50 rounded">
                                <AlertCircle size={12} /> Sin UUID
                              </span>
                            ) : txn.tipo_conciliacion === 'no_relacionado' ? (
                              <span className="inline-flex items-center gap-1 text-gray-600 text-xs px-2 py-1 bg-gray-100 rounded">
                                <X size={12} /> No Rel.
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-green-600 text-xs px-2 py-1 bg-green-50 rounded">
                                <Check size={12} /> Con UUID
                              </span>
                            )
                          ) : (
                            <span className="inline-flex items-center gap-1 text-yellow-600 text-xs px-2 py-1 bg-yellow-50 rounded">
                              <Clock size={14} /> Pendiente
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex justify-center gap-1 flex-wrap">
                            {!txn.conciliado && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-xs h-7"
                                  onClick={async () => {
                                    setSelectedTransaction(txn);
                                    setReconcileDialogOpen(true);
                                    // Load historical exchange rate for transaction date
                                    const account = bankAccounts.find(a => a.id === txn.bank_account_id);
                                    const moneda = txn.moneda || account?.moneda || 'MXN';
                                    if (moneda !== 'MXN') {
                                      const fechaMovimiento = txn.fecha_movimiento;
                                      const rateData = await getHistoricalFxRate(moneda, fechaMovimiento);
                                      setHistoricalFxRate(rateData);
                                    } else {
                                      setHistoricalFxRate({ tasa: 1.0, fecha: txn.fecha_movimiento });
                                    }
                                  }}
                                >
                                  <Link2 size={12} className="mr-1" />
                                  Con UUID
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-xs h-7 text-orange-600 border-orange-300 hover:bg-orange-50"
                                  onClick={() => openSinUUIDDialog(txn, 'sin_uuid')}
                                  title="Registrar como gasto/ingreso sin factura (comisiones, etc.)"
                                >
                                  Sin UUID
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-xs h-7 text-gray-600 border-gray-300 hover:bg-gray-50"
                                  onClick={() => handleQuickMarkWithoutUUID(txn, 'no_relacionado')}
                                  title="Marcar como no relacionado (transferencias internas)"
                                >
                                  No Rel.
                                </Button>
                              </>
                            )}
                            {txn.conciliado && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs h-7 text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => handleCancelReconciliation(txn)}
                                title="Cancelar conciliación para poder rehacerla"
                              >
                                <RotateCcw size={12} className="mr-1" />
                                Cancelar
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-blue-500 hover:text-blue-700"
                              onClick={() => handleEditTransaction(txn)}
                              title="Editar movimiento"
                            >
                              <Pencil size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-red-500 hover:text-red-700"
                              onClick={() => handleDelete(txn.id)}
                              title="Eliminar movimiento"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Connect Bank Dialog */}
      <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 size={20} />
              Conectar Banco (Belvo)
            </DialogTitle>
            <DialogDescription>
              Conecta tu cuenta bancaria para descargar movimientos automáticamente
            </DialogDescription>
          </DialogHeader>
          
          <BelvoConnectForm 
            bankAccounts={bankAccounts} 
            onSuccess={() => {
              setConnectDialogOpen(false);
              loadData();
              toast.success('Banco conectado exitosamente');
            }}
            onClose={() => setConnectDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Reconcile Dialog - Multi-select with balance tracking */}
      <Dialog open={reconcileDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setSelectedCfdis([]);
          setCfdiSearchTerm('');
          setHistoricalFxRate(null);  // Clear historical rate when dialog closes
          setCustomFxRate(null);      // Clear custom FX rate when dialog closes
          setIsEditingFxRate(false);  // Reset editing state
        }
        setReconcileDialogOpen(open);
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Conciliar Movimiento con CFDIs</DialogTitle>
            <DialogDescription>
              Selecciona los CFDIs que corresponden a este movimiento bancario
            </DialogDescription>
          </DialogHeader>
          
          {selectedTransaction && (
            <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
              {/* Movement info */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-blue-700">Movimiento Bancario:</p>
                    <p className="font-medium text-gray-800">{selectedTransaction.descripcion}</p>
                    <p className="text-sm text-gray-500">
                      {format(new Date(selectedTransaction.fecha_movimiento), 'dd/MM/yyyy')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-blue-700">Monto:</p>
                    <p className={`text-2xl font-bold font-mono ${selectedTransaction.tipo_movimiento === 'credito' ? 'text-green-600' : 'text-red-600'}`}>
                      ${selectedTransaction.monto.toLocaleString('es-MX', {minimumFractionDigits: 2})}
                      <span className="text-sm ml-1">{getReconciliationTotals().movimientoMoneda}</span>
                    </p>
                    {getReconciliationTotals().movimientoMoneda !== 'MXN' && (
                      <div className="text-sm text-gray-500">
                        <p>≈ ${getReconciliationTotals().movimientoMontoMXN.toLocaleString('es-MX', {minimumFractionDigits: 2})} MXN</p>
                        <div className="flex items-center justify-end gap-1 mt-1">
                          {isEditingFxRate ? (
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-500">TC:</span>
                              <Input
                                type="number"
                                step="0.0001"
                                value={customFxRate ?? getReconciliationTotals().tcUsado}
                                onChange={(e) => setCustomFxRate(parseFloat(e.target.value) || 0)}
                                className="w-24 h-6 text-xs px-1 py-0"
                                autoFocus
                              />
                              <button
                                onClick={() => setIsEditingFxRate(false)}
                                className="text-green-600 hover:text-green-800"
                                title="Confirmar"
                              >
                                <Check size={14} />
                              </button>
                              <button
                                onClick={() => {
                                  setCustomFxRate(null);
                                  setIsEditingFxRate(false);
                                }}
                                className="text-red-500 hover:text-red-700"
                                title="Cancelar y usar TC original"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <span className={`text-xs ${customFxRate !== null ? 'text-orange-600 font-medium' : 'text-blue-600'}`} title="Tipo de cambio">
                                (TC: {getReconciliationTotals().tcUsado.toFixed(4)} {customFxRate !== null ? '✏️' : '📅'})
                              </span>
                              <button
                                onClick={() => setIsEditingFxRate(true)}
                                className="text-gray-400 hover:text-blue-600"
                                title="Editar tipo de cambio"
                              >
                                <Pencil size={12} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Balance tracker - Improved presentation */}
              {selectedCfdis.length > 0 && (
                <div className="space-y-3">
                  {/* Main summary - Horizontal */}
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-gray-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      {/* Movimiento bancario */}
                      <div className="text-center">
                        <p className="text-xs text-blue-600 font-medium mb-1">Movimiento Bancario</p>
                        <p className="font-mono text-lg font-bold text-blue-700">
                          ${getReconciliationTotals().movimientoMonto.toLocaleString('es-MX', {minimumFractionDigits: 2})}
                          <span className="text-sm ml-1">{getReconciliationTotals().movimientoMoneda}</span>
                        </p>
                        {getReconciliationTotals().movimientoMoneda !== 'MXN' && (
                          <p className="text-xs text-gray-500">
                            ≈ ${getReconciliationTotals().movimientoMontoMXN.toLocaleString('es-MX', {minimumFractionDigits: 0})} MXN
                          </p>
                        )}
                      </div>
                      
                      {/* Minus sign */}
                      <div className="text-2xl text-gray-400 font-bold">−</div>
                      
                      {/* CFDIs seleccionados */}
                      <div className="text-center">
                        <p className="text-xs text-green-600 font-medium mb-1">CFDIs ({selectedCfdis.length})</p>
                        <p className="font-mono text-lg font-bold text-green-700">
                          ${getReconciliationTotals().cfdiTotalOriginal.toLocaleString('es-MX', {minimumFractionDigits: 2})}
                          <span className="text-sm ml-1">{getReconciliationTotals().movimientoMoneda}</span>
                        </p>
                        {getReconciliationTotals().movimientoMoneda !== 'MXN' && (
                          <p className="text-xs text-gray-500">
                            ≈ ${getReconciliationTotals().cfdiTotalMXN.toLocaleString('es-MX', {minimumFractionDigits: 0})} MXN
                          </p>
                        )}
                      </div>
                      
                      {/* Equals sign */}
                      <div className="text-2xl text-gray-400 font-bold">=</div>
                      
                      {/* Diferencia / Faltante */}
                      <div className={`text-center p-3 rounded-lg ${
                        Math.abs(getReconciliationTotals().diferenciaOriginal) < 0.01 
                          ? 'bg-green-100' 
                          : getReconciliationTotals().diferenciaOriginal > 0 
                            ? 'bg-yellow-100' 
                            : 'bg-red-100'
                      }`}>
                        <p className="text-xs font-medium mb-1">
                          {Math.abs(getReconciliationTotals().diferenciaOriginal) < 0.01 
                            ? '✓ Cuadra' 
                            : getReconciliationTotals().diferenciaOriginal > 0 
                              ? '⚠ Falta por cubrir'
                              : '⚠ Excede movimiento'}
                        </p>
                        <p className={`font-mono text-xl font-bold ${
                          Math.abs(getReconciliationTotals().diferenciaOriginal) < 0.01 
                            ? 'text-green-700' 
                            : 'text-yellow-700'
                        }`}>
                          {getReconciliationTotals().diferenciaOriginal > 0 ? '+' : ''}
                          ${getReconciliationTotals().diferenciaOriginal.toLocaleString('es-MX', {minimumFractionDigits: 2})}
                          <span className="text-sm ml-1">{getReconciliationTotals().movimientoMoneda}</span>
                        </p>
                        {getReconciliationTotals().movimientoMoneda !== 'MXN' && Math.abs(getReconciliationTotals().diferenciaOriginal) >= 0.01 && (
                          <p className="text-xs text-gray-500">
                            ≈ ${getReconciliationTotals().diferenciaMXN.toLocaleString('es-MX', {minimumFractionDigits: 0})} MXN
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Selected CFDIs - Detailed list showing progressive sum */}
              {selectedCfdis.length > 0 && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-sm font-medium text-green-700">CFDIs seleccionados (Pagos Parciales Permitidos):</p>
                    <p className="text-xs text-gray-500">
                      Total a aplicar: ${getReconciliationTotals().cfdiTotalOriginal.toLocaleString('es-MX', {minimumFractionDigits: 2})} {getReconciliationTotals().movimientoMoneda}
                    </p>
                  </div>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {selectedCfdis.map((cfdi, idx) => {
                      const cfdiMoneda = cfdi.moneda || 'MXN';
                      const movMoneda = getReconciliationTotals().movimientoMoneda;
                      const tc = getReconciliationTotals().tcUsado;
                      const saldoPendiente = cfdi.saldo_pendiente ?? (cfdi.total - (cfdi.monto_pagado || 0));
                      const montoAplicar = getMontoAplicar(cfdi);
                      const yaPagado = cfdi.monto_pagado || 0;
                      
                      // Convert CFDI to movement currency for display
                      let montoEnMovMoneda = montoAplicar;
                      if (cfdiMoneda !== movMoneda) {
                        if (cfdiMoneda === 'MXN' && movMoneda !== 'MXN') {
                          montoEnMovMoneda = montoAplicar / tc;
                        } else if (cfdiMoneda !== 'MXN' && movMoneda === 'MXN') {
                          montoEnMovMoneda = montoAplicar * tc;
                        }
                      }
                      
                      return (
                        <div key={cfdi.id} className="bg-white p-3 rounded border border-green-100">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <button 
                                onClick={() => toggleCfdiSelection(cfdi)}
                                className="text-red-500 hover:text-red-700 shrink-0"
                                title="Quitar CFDI"
                              >
                                <X size={14} />
                              </button>
                              <div>
                                <span className="font-medium text-sm truncate block">{cfdi.receptor_nombre || cfdi.emisor_nombre}</span>
                                <span className="text-xs text-gray-400">UUID: {cfdi.uuid?.substring(0, 8)}...</span>
                              </div>
                            </div>
                            <div className="text-right shrink-0 ml-2">
                              <div className="text-xs text-gray-500">Total CFDI:</div>
                              <span className="font-mono text-sm text-gray-700">${cfdi.total?.toLocaleString('es-MX', {minimumFractionDigits: 2})} {cfdiMoneda}</span>
                            </div>
                          </div>
                          
                          {/* Show payment history if partial payments exist */}
                          {yaPagado > 0 && (
                            <div className="flex justify-between items-center text-xs mb-2 px-2 py-1 bg-yellow-50 rounded">
                              <div>
                                <span className="text-yellow-700">Ya pagado anteriormente:</span>
                                <span className="font-mono text-yellow-700 ml-2">${yaPagado.toLocaleString('es-MX', {minimumFractionDigits: 2})} {cfdiMoneda}</span>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  loadPaymentHistory(cfdi);
                                }}
                                className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                              >
                                <History size={12} />
                                Ver detalle
                              </button>
                            </div>
                          )}
                          
                          {/* Editable partial amount */}
                          <div className="flex items-center gap-2 mt-2">
                            <div className="flex-1">
                              <Label className="text-xs text-green-700">Monto a aplicar ahora:</Label>
                              <div className="flex items-center gap-1 mt-1">
                                <span className="text-sm text-gray-500">$</span>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max={saldoPendiente}
                                  value={montoAplicar}
                                  onChange={(e) => updateMontoParcial(cfdi.id, e.target.value)}
                                  className="h-8 text-sm font-mono w-32"
                                  data-testid={`monto-parcial-${cfdi.id}`}
                                />
                                <span className="text-xs text-gray-500">{cfdiMoneda}</span>
                                {cfdiMoneda !== movMoneda && (
                                  <span className="text-xs text-blue-500">
                                    (≈${montoEnMovMoneda?.toLocaleString('es-MX', {minimumFractionDigits: 2})} {movMoneda})
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-500">Saldo pendiente:</div>
                              <span className={`font-mono text-sm ${saldoPendiente > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                                ${saldoPendiente.toLocaleString('es-MX', {minimumFractionDigits: 2})} {cfdiMoneda}
                              </span>
                            </div>
                          </div>
                          
                          {/* Category and Subcategory selection */}
                          <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-100">
                            <div>
                              <Label className="text-xs text-gray-600">Categoría *</Label>
                              <Select
                                value={cfdiCategories[cfdi.id]?.categoria_id || ''}
                                onValueChange={(value) => {
                                  setCfdiCategories(prev => ({
                                    ...prev,
                                    [cfdi.id]: { ...prev[cfdi.id], categoria_id: value, subcategoria: '' }
                                  }));
                                }}
                              >
                                <SelectTrigger className="h-8 text-sm mt-1" data-testid={`categoria-${cfdi.id}`}>
                                  <SelectValue placeholder="Seleccionar categoría" />
                                </SelectTrigger>
                                <SelectContent>
                                  {getCategoriesForCfdi(cfdi).map(cat => (
                                    <SelectItem key={cat.id} value={cat.id}>{cat.nombre}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-600 flex items-center gap-1">
                                Subcategoría
                                {cfdiCategories[cfdi.id]?.categoria_id && (
                                  <span className="text-purple-600 text-[10px]">(escribe para crear nueva)</span>
                                )}
                              </Label>
                              <div className="relative">
                                <Input
                                  type="text"
                                  list={`subcategorias-${cfdi.id}`}
                                  placeholder={cfdiCategories[cfdi.id]?.categoria_id ? "Seleccionar o crear..." : "Primero selecciona categoría"}
                                  value={cfdiCategories[cfdi.id]?.subcategoria || ''}
                                  onChange={(e) => {
                                    setCfdiCategories(prev => ({
                                      ...prev,
                                      [cfdi.id]: { ...prev[cfdi.id], subcategoria: e.target.value }
                                    }));
                                  }}
                                  disabled={!cfdiCategories[cfdi.id]?.categoria_id}
                                  className="h-8 text-sm mt-1"
                                  data-testid={`subcategoria-${cfdi.id}`}
                                />
                                <datalist id={`subcategorias-${cfdi.id}`}>
                                  {getSubcategoriesForCategory(cfdiCategories[cfdi.id]?.categoria_id).map(sub => (
                                    <option key={sub.id} value={sub.nombre} />
                                  ))}
                                </datalist>
                              </div>
                            </div>
                          </div>
                          
                          {/* Remaining after this payment */}
                          {montoAplicar < saldoPendiente && montoAplicar > 0 && (
                            <div className="mt-2 text-xs text-blue-600 bg-blue-50 p-1 rounded text-center">
                              Después de este pago quedará pendiente: ${(saldoPendiente - montoAplicar).toLocaleString('es-MX', {minimumFractionDigits: 2})} {cfdiMoneda}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Search CFDIs with Auto-Match Button */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Buscar por cliente, RFC o UUID..."
                    value={cfdiSearchTerm}
                    onChange={(e) => setCfdiSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-purple-600 border-purple-300 hover:bg-purple-50"
                  data-testid="sugerir-btn"
                  onClick={async () => {
                    if (!selectedTransaction) {
                      toast.error('No hay movimiento seleccionado');
                      return;
                    }
                    try {
                      toast.loading('Buscando coincidencias...', { id: 'auto-match' });
                      const res = await api.get(`/bank-transactions/${selectedTransaction.id}/match-cfdi?tolerance_days=90`);
                      toast.dismiss('auto-match');
                      
                      // Get matches from response - handle both response formats
                      const bestMatch = res.data.best_match;
                      const allMatches = res.data.all_matches || res.data.matches || [];
                      
                      console.log('Match response:', { bestMatch, allMatches, total: res.data.total_candidates });
                      
                      if (bestMatch) {
                        // bestMatch contains the full CFDI data with id, score, match_reasons
                        const cfdiId = bestMatch.id || bestMatch.cfdi_id;
                        const cfdiToSelect = cfdis.find(c => c.id === cfdiId);
                        
                        console.log('Best match ID:', cfdiId);
                        console.log('CFDI found in local state:', cfdiToSelect?.id);
                        
                        if (cfdiToSelect && !selectedCfdis.find(c => c.id === cfdiToSelect.id)) {
                          // Clear search term to show all CFDIs
                          setCfdiSearchTerm('');
                          
                          // Add to selection
                          setSelectedCfdis(prev => [...prev, cfdiToSelect]);
                          
                          // Initialize partial amount
                          const saldoPendiente = cfdiToSelect.saldo_pendiente ?? (cfdiToSelect.total - (cfdiToSelect.monto_pagado || 0));
                          setMontosParciales(prevMontos => ({
                            ...prevMontos,
                            [cfdiToSelect.id]: Math.max(0, saldoPendiente)
                          }));
                          
                          // Highlight the selected CFDI
                          setHighlightedCfdiId(cfdiId);
                          setTimeout(() => setHighlightedCfdiId(null), 3000);
                          
                          // Scroll to the CFDI after a short delay to let React render
                          setTimeout(() => {
                            const element = document.querySelector(`[data-cfdi-id="${cfdiId}"]`);
                            if (element) {
                              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                          }, 100);
                          
                          const nombre = bestMatch.emisor_nombre || bestMatch.receptor_nombre || 'CFDI';
                          const razones = bestMatch.match_reasons?.join(', ') || '';
                          toast.success(`✓ Auto-seleccionado: ${nombre} - Score: ${bestMatch.score} (${razones})`);
                        } else if (!cfdiToSelect) {
                          const nombre = bestMatch.emisor_nombre || bestMatch.receptor_nombre || 'CFDI';
                          toast.warning(`Mejor coincidencia: ${nombre} ($${bestMatch.total?.toLocaleString()}) - Score: ${bestMatch.score} - NO está en la lista (puede estar conciliado o filtrado)`);
                        } else {
                          toast.info('El CFDI sugerido ya está seleccionado');
                        }
                      } else if (allMatches.length > 0) {
                        // Try first match from array
                        const firstMatch = allMatches[0];
                        const cfdiData = firstMatch.cfdi || firstMatch;
                        const cfdiId = cfdiData.id || cfdiData.cfdi_id;
                        const cfdiToSelect = cfdis.find(c => c.id === cfdiId);
                        
                        console.log('First match ID:', cfdiId);
                        console.log('CFDI found in local state:', cfdiToSelect?.id);
                        
                        if (cfdiToSelect && !selectedCfdis.find(c => c.id === cfdiToSelect.id)) {
                          // Clear search term to show all CFDIs
                          setCfdiSearchTerm('');
                          
                          // Add to selection
                          setSelectedCfdis(prev => [...prev, cfdiToSelect]);
                          
                          // Initialize partial amount
                          const saldoPendiente = cfdiToSelect.saldo_pendiente ?? (cfdiToSelect.total - (cfdiToSelect.monto_pagado || 0));
                          setMontosParciales(prevMontos => ({
                            ...prevMontos,
                            [cfdiToSelect.id]: Math.max(0, saldoPendiente)
                          }));
                          
                          // Highlight the selected CFDI
                          setHighlightedCfdiId(cfdiId);
                          setTimeout(() => setHighlightedCfdiId(null), 3000);
                          
                          // Scroll to the CFDI
                          setTimeout(() => {
                            const element = document.querySelector(`[data-cfdi-id="${cfdiId}"]`);
                            if (element) {
                              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                          }, 100);
                          
                          const nombre = cfdiData.emisor_nombre || cfdiData.receptor_nombre || 'CFDI';
                          const score = firstMatch.score || cfdiData.score;
                          const razones = (firstMatch.match_reasons || cfdiData.match_reasons || []).join(', ');
                          toast.success(`✓ Auto-seleccionado: ${nombre} - Score: ${score} (${razones})`);
                        } else if (!cfdiToSelect) {
                          const nombre = cfdiData.emisor_nombre || cfdiData.receptor_nombre || 'CFDI';
                          toast.warning(`Coincidencia encontrada: ${nombre} ($${cfdiData.total?.toLocaleString()}) pero no está en la lista (puede estar conciliado o filtrado)`);
                        }
                      } else {
                        toast.info('No se encontraron coincidencias automáticas para este movimiento');
                      }
                    } catch (err) {
                      toast.dismiss('auto-match');
                      console.error('Auto-match error:', err);
                      toast.error(err.response?.data?.detail || 'Error buscando coincidencias');
                    }
                  }}
                >
                  <Sparkles size={14} className="mr-1" />
                  Sugerir
                </Button>
              </div>

              {/* CFDIs list */}
              <div className="flex-1 overflow-y-auto space-y-2 min-h-[200px] max-h-[300px]">
                <p className="text-sm font-medium sticky top-0 bg-white py-1">CFDIs disponibles:</p>
                {cfdis
                  .filter(c => c.estado_conciliacion !== 'conciliado')
                  .filter(c => {
                    if (!cfdiSearchTerm) return true;
                    const search = cfdiSearchTerm.toLowerCase();
                    return (
                      c.receptor_nombre?.toLowerCase().includes(search) ||
                      c.emisor_nombre?.toLowerCase().includes(search) ||
                      c.receptor_rfc?.toLowerCase().includes(search) ||
                      c.emisor_rfc?.toLowerCase().includes(search) ||
                      c.uuid?.toLowerCase().includes(search)
                    );
                  })
                  .length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No hay CFDIs que coincidan</p>
                ) : (
                  cfdis
                    .filter(c => c.estado_conciliacion !== 'conciliado')
                    .filter(c => {
                      if (!cfdiSearchTerm) return true;
                      const search = cfdiSearchTerm.toLowerCase();
                      return (
                        c.receptor_nombre?.toLowerCase().includes(search) ||
                        c.emisor_nombre?.toLowerCase().includes(search) ||
                        c.receptor_rfc?.toLowerCase().includes(search) ||
                        c.emisor_rfc?.toLowerCase().includes(search) ||
                        c.uuid?.toLowerCase().includes(search)
                      );
                    })
                    .map(cfdi => {
                      const isSelected = selectedCfdis.some(c => c.id === cfdi.id);
                      const isHighlighted = highlightedCfdiId === cfdi.id;
                      return (
                        <div
                          key={cfdi.id}
                          data-cfdi-id={cfdi.id}
                          className={`p-3 border rounded-lg cursor-pointer transition-all duration-300 ${
                            isHighlighted
                              ? 'bg-yellow-100 border-yellow-500 ring-2 ring-yellow-400 shadow-lg animate-pulse'
                              : isSelected 
                                ? 'bg-green-100 border-green-400' 
                                : 'hover:bg-blue-50 border-gray-200'
                          }`}
                          onClick={() => toggleCfdiSelection(cfdi)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <input 
                                  type="checkbox" 
                                  checked={isSelected}
                                  onChange={() => {}}
                                  className="w-4 h-4 text-green-600"
                                />
                                <div>
                                  <p className="font-medium text-gray-800">
                                    {cfdi.tipo_cfdi === 'ingreso' ? (
                                      <>
                                        <span className="text-green-700">Cliente: </span>
                                        {cfdi.receptor_nombre}
                                      </>
                                    ) : cfdi.tipo_cfdi === 'nomina' || cfdi.is_nomina ? (
                                      <>
                                        <span className="text-purple-700">Empleado: </span>
                                        {cfdi.receptor_nombre}
                                        <span className="text-xs text-gray-400 ml-2">({cfdi.emisor_nombre?.substring(0, 20)}...)</span>
                                      </>
                                    ) : (
                                      <>
                                        <span className="text-red-700">Proveedor: </span>
                                        {cfdi.emisor_nombre}
                                      </>
                                    )}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    RFC: {cfdi.tipo_cfdi === 'ingreso' || cfdi.tipo_cfdi === 'nomina' || cfdi.is_nomina ? cfdi.receptor_rfc : cfdi.emisor_rfc}
                                  </p>
                                  <p className="text-xs text-gray-400">
                                    {cfdi.tipo_cfdi === 'nomina' || cfdi.is_nomina ? '💼 NÓMINA' : cfdi.tipo_cfdi?.toUpperCase()} | {cfdi.fecha_emision?.slice(0, 10)} | {cfdi.uuid?.slice(0, 8)}...
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-mono font-bold ${cfdi.tipo_cfdi === 'ingreso' ? 'text-green-600' : 'text-red-600'}`}>
                                ${cfdi.total?.toLocaleString('es-MX', {minimumFractionDigits: 2})}
                              </p>
                              <p className="text-xs text-gray-500">{cfdi.moneda}</p>
                              {/* Show saldo pendiente and history button if there are previous payments */}
                              {(cfdi.monto_pagado > 0 || cfdi.monto_cobrado > 0 || cfdi.saldo_pendiente < cfdi.total) && (
                                <div className="mt-1">
                                  <p className="text-xs text-amber-600">
                                    Saldo: ${cfdi.saldo_pendiente?.toLocaleString('es-MX', {minimumFractionDigits: 2})}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      loadPaymentHistory(cfdi);
                                    }}
                                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-1"
                                  >
                                    <History size={12} />
                                    Ver historial
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                )}
              </div>
            </div>
          )}

          <DialogFooter className="flex justify-between items-center border-t pt-4">
            <div className="text-sm text-gray-500">
              {selectedCfdis.length > 0 && (
                <span>
                  {Math.abs(getReconciliationTotals().diferenciaOriginal) < 0.01 ? (
                    <span className="text-green-600 font-medium">✓ Los montos cuadran perfectamente</span>
                  ) : (
                    <span className="text-yellow-600">
                      Falta: ${Math.abs(getReconciliationTotals().diferenciaOriginal).toLocaleString('es-MX', {minimumFractionDigits: 2})} {getReconciliationTotals().movimientoMoneda}
                      {getReconciliationTotals().movimientoMoneda !== 'MXN' && (
                        <span className="text-gray-400 ml-1">
                          (≈ ${Math.abs(getReconciliationTotals().diferenciaMXN).toLocaleString('es-MX', {minimumFractionDigits: 0})} MXN)
                        </span>
                      )}
                    </span>
                  )}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => {
                setReconcileDialogOpen(false);
                setSelectedTransaction(null);
                setSelectedCfdis([]);
                setCfdiSearchTerm('');
              }}>
                Cancelar
              </Button>
              <Button 
                onClick={handleConfirmReconciliation}
                disabled={selectedCfdis.length === 0}
                className="bg-green-600 hover:bg-green-700"
              >
                Confirmar Conciliación ({selectedCfdis.length})
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog - Select Account */}
      <Dialog open={importDialogOpen} onOpenChange={(open) => {
        if (!open && !importing) {
          setImportDialogOpen(false);
          setImportFile(null);
          setImportAccountId('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload size={20} />
              Importar Estado de Cuenta
            </DialogTitle>
            <DialogDescription>
              Selecciona la cuenta bancaria donde se registrarán los movimientos
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {importFile && (
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                <FileSpreadsheet size={20} className="text-blue-600" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{importFile.name}</p>
                  <p className="text-xs text-gray-500">{(importFile.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-medium">Cuenta Bancaria Destino *</Label>
              <Select value={importAccountId} onValueChange={setImportAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar cuenta..." />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.map(acc => (
                    <SelectItem key={acc.id} value={acc.id}>
                      <div className="flex items-center gap-2">
                        <Building2 size={14} className="text-gray-500" />
                        <span className="font-medium">{acc.banco}</span>
                        <span className="text-gray-500">-</span>
                        <span>{acc.nombre}</span>
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                          acc.moneda === 'USD' ? 'bg-blue-100 text-blue-700' :
                          acc.moneda === 'EUR' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {acc.moneda}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Los movimientos se importarán a esta cuenta. Asegúrate de seleccionar la cuenta correcta según la moneda del estado de cuenta.
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Importante:</strong> Verifica que la moneda del archivo coincida con la cuenta seleccionada.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setImportDialogOpen(false);
                setImportFile(null);
                setImportAccountId('');
              }}
              disabled={importing}
            >
              Cancelar
            </Button>
            <Button 
              onClick={processImport} 
              disabled={!importAccountId || importing}
              className="bg-[#0F172A]"
            >
              {importing ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <Upload size={16} className="mr-2" />
                  Importar Movimientos
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import PDF Dialog */}
      <Dialog open={importPdfDialogOpen} onOpenChange={(open) => {
        if (!open && !importingPdf && !loadingPreview) {
          setImportPdfDialogOpen(false);
          setPdfFile(null);
          setPdfAccountId('');
          setPdfPreview(null);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} className="text-red-600" />
              Importar Estado de Cuenta PDF
            </DialogTitle>
            <DialogDescription>
              Vista previa de los movimientos detectados en tu estado de cuenta
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* File Info */}
            {pdfFile && (
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-200">
                <FileText size={20} className="text-red-600" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{pdfFile.name}</p>
                  <p className="text-xs text-gray-500">{(pdfFile.size / 1024).toFixed(1)} KB</p>
                </div>
                {pdfPreview?.banco_detectado && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                    {pdfPreview.banco_detectado}
                  </span>
                )}
              </div>
            )}

            {/* Loading state */}
            {loadingPreview && (
              <div className="flex flex-col items-center justify-center py-8">
                <RefreshCw size={32} className="text-red-600 animate-spin mb-3" />
                <p className="text-sm text-gray-600">Analizando PDF...</p>
              </div>
            )}

            {/* Preview Results */}
            {pdfPreview && !loadingPreview && (
              <>
                {/* ACCOUNT SELECTION - FIRST and PROMINENT */}
                {pdfPreview.total_movimientos > 0 && (
                  <div className="space-y-2 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                    <Label className="text-sm font-bold text-yellow-800 flex items-center gap-2">
                      <AlertCircle size={16} />
                      Selecciona la Cuenta Bancaria *
                    </Label>
                    <Select value={pdfAccountId} onValueChange={setPdfAccountId}>
                      <SelectTrigger className="bg-white border-yellow-400">
                        <SelectValue placeholder="⚠️ Seleccionar cuenta destino..." />
                      </SelectTrigger>
                      <SelectContent>
                        {bankAccounts.map(acc => (
                          <SelectItem key={acc.id} value={acc.id}>
                            <div className="flex items-center gap-2">
                              <Building2 size={14} className="text-gray-500" />
                              <span className="font-medium">{acc.banco}</span>
                              <span className="text-gray-500">-</span>
                              <span>{acc.nombre}</span>
                              <span className={`ml-2 text-xs px-2 py-0.5 rounded font-bold ${
                                acc.moneda === 'USD' ? 'bg-blue-100 text-blue-700' :
                                acc.moneda === 'EUR' ? 'bg-purple-100 text-purple-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {acc.moneda}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-yellow-700">
                      Los movimientos se asociarán a esta cuenta. Movimientos duplicados serán omitidos.
                    </p>
                  </div>
                )}

                {/* Summary Cards */}
                {pdfPreview.total_movimientos > 0 ? (
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-700">{pdfPreview.total_movimientos}</p>
                      <p className="text-xs text-blue-600">Movimientos</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-green-700">
                        ${pdfPreview.total_depositos?.toLocaleString('es-MX', {minimumFractionDigits: 2})}
                      </p>
                      <p className="text-xs text-green-600">Depósitos</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-red-700">
                        ${pdfPreview.total_retiros?.toLocaleString('es-MX', {minimumFractionDigits: 2})}
                      </p>
                      <p className="text-xs text-red-600">Retiros</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <p className={`text-lg font-bold ${pdfPreview.flujo_neto >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        ${Math.abs(pdfPreview.flujo_neto || 0).toLocaleString('es-MX', {minimumFractionDigits: 2})}
                      </p>
                      <p className="text-xs text-purple-600">Flujo Neto</p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                    <AlertCircle size={32} className="text-yellow-600 mx-auto mb-2" />
                    <p className="text-sm text-yellow-800 font-medium">No se encontraron movimientos</p>
                    <p className="text-xs text-yellow-700 mt-1">
                      El formato del PDF no es compatible. Intenta con la plantilla Excel.
                    </p>
                  </div>
                )}

                {/* Transaction Preview Table */}
                {pdfPreview.transactions?.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 border-b">
                      <p className="text-sm font-medium text-gray-700">Vista Previa de Movimientos</p>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Fecha</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Descripción</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Monto</th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Tipo</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {pdfPreview.transactions.slice(0, 15).map((txn, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                                {txn.fecha}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-900 truncate max-w-[200px]">
                                {txn.descripcion}
                              </td>
                              <td className={`px-3 py-2 text-xs font-medium text-right ${
                                txn.tipo === 'credito' ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {txn.tipo === 'credito' ? '+' : '-'}${txn.monto?.toLocaleString('es-MX', {minimumFractionDigits: 2})}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-2 py-0.5 text-xs rounded ${
                                  txn.tipo === 'credito' 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  {txn.tipo_display}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {pdfPreview.transactions.length > 15 && (
                        <div className="px-3 py-2 bg-gray-50 text-center text-xs text-gray-500">
                          ... y {pdfPreview.transactions.length - 15} movimientos más
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setImportPdfDialogOpen(false);
                setPdfFile(null);
                setPdfAccountId('');
                setPdfPreview(null);
              }}
              disabled={importingPdf || loadingPreview}
            >
              Cancelar
            </Button>
            <Button 
              onClick={processPdfImport} 
              disabled={!pdfAccountId || importingPdf || loadingPreview || !pdfPreview?.total_movimientos}
              className="bg-red-600 hover:bg-red-700"
            >
              {importingPdf ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <Check size={16} className="mr-2" />
                  Confirmar Importación ({pdfPreview?.total_movimientos || 0})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Transaction Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setEditDialogOpen(false);
          setEditingTransaction(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil size={20} />
              Editar Movimiento Bancario
            </DialogTitle>
            <DialogDescription>
              Modifica los datos del movimiento seleccionado
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleUpdateTransaction} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Cuenta Bancaria</Label>
              <Select 
                value={editFormData.bank_account_id} 
                onValueChange={(v) => setEditFormData({...editFormData, bank_account_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar cuenta..." />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.map(acc => (
                    <SelectItem key={acc.id} value={acc.id}>
                      <div className="flex items-center gap-2">
                        <Building2 size={14} />
                        {acc.banco} - {acc.nombre} ({acc.moneda})
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input
                value={editFormData.descripcion}
                onChange={(e) => setEditFormData({...editFormData, descripcion: e.target.value})}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monto</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editFormData.monto}
                  onChange={(e) => setEditFormData({...editFormData, monto: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo de Movimiento</Label>
                <Select 
                  value={editFormData.tipo_movimiento} 
                  onValueChange={(v) => setEditFormData({...editFormData, tipo_movimiento: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credito">Depósito (Crédito)</SelectItem>
                    <SelectItem value="debito">Retiro (Débito)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha Movimiento</Label>
                <Input
                  type="datetime-local"
                  value={editFormData.fecha_movimiento}
                  onChange={(e) => setEditFormData({...editFormData, fecha_movimiento: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Referencia</Label>
                <Input
                  value={editFormData.referencia}
                  onChange={(e) => setEditFormData({...editFormData, referencia: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Input
                value={editFormData.notas}
                onChange={(e) => setEditFormData({...editFormData, notas: e.target.value})}
                placeholder="Notas adicionales..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#0F172A]">
                Guardar Cambios
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Sin UUID Dialog - Register expense/income without CFDI */}
      <Dialog open={sinUUIDDialogOpen} onOpenChange={setSinUUIDDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle size={20} className="text-orange-500" />
              Registrar Movimiento Sin UUID
            </DialogTitle>
            <DialogDescription>
              Registra este movimiento como gasto o ingreso sin factura CFDI.
              Se creará automáticamente un registro en Cobranza y Pagos.
            </DialogDescription>
          </DialogHeader>
          
          {sinUUIDTransaction && (
            <div className="space-y-4">
              {/* Transaction Summary */}
              <div className="p-3 bg-gray-50 rounded-lg border">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-medium text-sm">{sinUUIDTransaction.descripcion?.substring(0, 50)}</div>
                    <div className="text-xs text-gray-500">
                      {sinUUIDTransaction.fecha_movimiento ? format(new Date(sinUUIDTransaction.fecha_movimiento), 'dd MMM yyyy', { locale: es }) : ''}
                    </div>
                  </div>
                  <div className={`text-lg font-bold ${sinUUIDTransaction.tipo_movimiento === 'credito' ? 'text-green-600' : 'text-red-600'}`}>
                    {sinUUIDTransaction.tipo_movimiento === 'credito' ? '+' : '-'}
                    ${sinUUIDTransaction.monto?.toLocaleString('es-MX', {minimumFractionDigits: 2})} {sinUUIDTransaction.moneda || 'MXN'}
                  </div>
                </div>
              </div>

              {/* Type Selection */}
              <div className="space-y-2">
                <Label>Tipo de Conciliación</Label>
                <Select 
                  value={sinUUIDFormData.tipo_conciliacion} 
                  onValueChange={(v) => setSinUUIDFormData({...sinUUIDFormData, tipo_conciliacion: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sin_uuid">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                        Sin UUID - Gasto/Ingreso sin factura
                      </div>
                    </SelectItem>
                    <SelectItem value="no_relacionado">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-gray-400"></span>
                        No Relacionado - Movimiento interno
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Category Selection */}
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Select 
                  value={sinUUIDFormData.categoria} 
                  onValueChange={(v) => setSinUUIDFormData({...sinUUIDFormData, categoria: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar categoría..." />
                  </SelectTrigger>
                  <SelectContent>
                    {getAllCategories().map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.isUser && <span className="text-blue-600 mr-1">●</span>}
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  <span className="text-blue-600">●</span> = Categorías personalizadas del módulo Categorías
                </p>
              </div>

              {/* Concept */}
              <div className="space-y-2">
                <Label>Concepto</Label>
                <Input
                  value={sinUUIDFormData.concepto}
                  onChange={(e) => setSinUUIDFormData({...sinUUIDFormData, concepto: e.target.value})}
                  placeholder="Descripción del gasto/ingreso..."
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notas (opcional)</Label>
                <Input
                  value={sinUUIDFormData.notas}
                  onChange={(e) => setSinUUIDFormData({...sinUUIDFormData, notas: e.target.value})}
                  placeholder="Notas adicionales..."
                />
              </div>

              {/* Info banner */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <strong>📌 Nota:</strong> Este movimiento se registrará como {sinUUIDTransaction.tipo_movimiento === 'credito' ? 'cobro' : 'pago'} 
                {' '}en el módulo &quot;Cobranza y Pagos&quot; para mantener el control de flujo de efectivo.
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setSinUUIDDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  type="button" 
                  className="bg-orange-600 hover:bg-orange-700"
                  onClick={handleMarkWithoutUUID}
                >
                  Registrar y Conciliar
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment History Dialog */}
      <Dialog open={paymentHistoryDialogOpen} onOpenChange={setPaymentHistoryDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History size={20} className="text-blue-600" />
              Historial de Pagos - CFDI
            </DialogTitle>
          </DialogHeader>
          
          {loadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="animate-spin text-blue-500 mr-2" size={24} />
              <span>Cargando historial...</span>
            </div>
          ) : paymentHistory ? (
            <div className="space-y-4">
              {/* CFDI Summary */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">CFDI</p>
                    <p className="font-semibold text-sm">{paymentHistory.emisor_nombre || paymentHistory.receptor_nombre}</p>
                    <p className="text-xs text-gray-400">UUID: {paymentHistory.cfdi_uuid?.substring(0, 8)}...</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Monto Total</p>
                    <p className="font-bold text-lg">${paymentHistory.cfdi_total?.toLocaleString('es-MX', {minimumFractionDigits: 2})} {paymentHistory.cfdi_moneda}</p>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-green-600">Pagado: ${paymentHistory.total_pagado?.toLocaleString('es-MX', {minimumFractionDigits: 2})}</span>
                    <span className={paymentHistory.saldo_pendiente > 0 ? 'text-amber-600' : 'text-green-600'}>
                      Pendiente: ${paymentHistory.saldo_pendiente?.toLocaleString('es-MX', {minimumFractionDigits: 2})}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full ${paymentHistory.porcentaje_pagado >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                      style={{ width: `${Math.min(100, paymentHistory.porcentaje_pagado || 0)}%` }}
                    />
                  </div>
                  <p className="text-center text-xs text-gray-500 mt-1">
                    {paymentHistory.porcentaje_pagado?.toFixed(1)}% pagado
                  </p>
                </div>
              </div>
              
              {/* Payment List */}
              <div>
                <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
                  <FileText size={16} />
                  Detalle de Pagos ({paymentHistory.total_pagos})
                </h4>
                
                {paymentHistory.pagos?.length > 0 ? (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {paymentHistory.pagos.map((pago, idx) => (
                      <div key={pago.id || idx} className="bg-white border rounded-lg p-3 hover:shadow-sm transition-shadow">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                pago.tipo === 'cobro' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {pago.tipo === 'cobro' ? 'Cobro' : 'Pago'}
                              </span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                pago.estatus === 'completado' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {pago.estatus}
                              </span>
                            </div>
                            <p className="font-medium mt-1">${pago.monto?.toLocaleString('es-MX', {minimumFractionDigits: 2})} {pago.moneda}</p>
                            {pago.concepto && <p className="text-xs text-gray-500">{pago.concepto}</p>}
                          </div>
                          <div className="text-right text-xs text-gray-500">
                            {pago.fecha && (
                              <p>{format(new Date(pago.fecha), 'dd/MM/yyyy', { locale: es })}</p>
                            )}
                          </div>
                        </div>
                        
                        {/* Bank Transaction Details */}
                        {(pago.descripcion_bancaria || pago.referencia_bancaria || pago.banco) && (
                          <div className="mt-2 pt-2 border-t border-dashed text-xs text-gray-600">
                            <div className="flex items-center gap-1 text-blue-600 mb-1">
                              <Building2 size={12} />
                              <span>Referencia Bancaria</span>
                            </div>
                            {pago.banco && (
                              <p><strong>Banco:</strong> {pago.banco} - {pago.cuenta}</p>
                            )}
                            {pago.descripcion_bancaria && (
                              <p><strong>Descripción:</strong> {pago.descripcion_bancaria}</p>
                            )}
                            {pago.referencia_bancaria && (
                              <p><strong>Referencia:</strong> {pago.referencia_bancaria}</p>
                            )}
                            {pago.fecha_movimiento_banco && (
                              <p><strong>Fecha Mov:</strong> {format(new Date(pago.fecha_movimiento_banco), 'dd/MM/yyyy', { locale: es })}</p>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <Clock size={32} className="mx-auto mb-2 opacity-50" />
                    <p>No hay pagos registrados para este CFDI</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle size={32} className="mx-auto mb-2" />
              <p>Error al cargar el historial de pagos</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentHistoryDialogOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Transactions Dialog */}
      <AlertDialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 size={20} />
              ¿Eliminar {filteredTransactions.length} movimientos bancarios?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-3">
                <p>
                  Estás a punto de eliminar <strong>{filteredTransactions.length}</strong> movimientos bancarios
                  {filteredTransactions.length !== bankTransactions.length && (
                    <span className="text-orange-600"> (de {bankTransactions.length} totales, basado en los filtros activos)</span>
                  )}
                </p>
                
                {(filterAccount !== 'all' || filterStatus !== 'all') && (
                  <Alert className="bg-orange-50 border-orange-200">
                    <AlertCircle className="h-4 w-4 text-orange-600" />
                    <AlertDescription>
                      <strong>Filtros activos:</strong>
                      <ul className="list-disc list-inside mt-1">
                        {filterAccount !== 'all' && (
                          <li>Cuenta: {bankAccounts.find(a => a.id === filterAccount)?.nombre || filterAccount}</li>
                        )}
                        {filterStatus !== 'all' && (
                          <li>Estado: {filterStatus}</li>
                        )}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
                
                <p className="text-red-600 font-semibold">
                  ⚠️ Esta acción eliminará los movimientos y sus conciliaciones asociadas. No se puede deshacer.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingAll}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteAllTransactions}
              className="bg-red-600 hover:bg-red-700"
              disabled={deletingAll}
              data-testid="confirm-delete-all-transactions-btn"
            >
              {deletingAll ? (
                <>
                  <Loader2 size={14} className="mr-1 animate-spin" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 size={14} className="mr-1" />
                  Eliminar {filteredTransactions.length} Movimientos
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ===== MODAL IMPORTAR AMEX ===== */}
      <Dialog open={amexDialogOpen} onOpenChange={(o) => { if (!o) { setAmexDialogOpen(false); setAmexResult(null); setAmexFile(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <svg width="36" height="22" viewBox="0 0 36 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="36" height="22" rx="3" fill="#007BC1"/>
                <text x="3" y="15" fontFamily="Arial" fontWeight="bold" fontSize="11" fill="white">AMEX</text>
              </svg>
              Importar Estado de Cuenta AMEX
            </DialogTitle>
            <DialogDescription>
              Sube el CSV de actividad descargado de americanexpress.com.mx
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 space-y-1">
              <p className="font-medium">¿Cómo descargar el CSV de AMEX?</p>
              <ol className="list-decimal list-inside space-y-0.5 text-xs text-blue-700">
                <li>Entra a <strong>americanexpress.com.mx</strong></li>
                <li>Ve a <strong>Cuenta → Actividad de la cuenta</strong></li>
                <li>Selecciona el rango de fechas</li>
                <li>Haz clic en <strong>Descargar → CSV</strong></li>
              </ol>
            </div>
            {amexFile && (
              <div className="flex items-center gap-2 bg-gray-50 rounded p-2 text-sm">
                <FileText size={16} className="text-blue-600" />
                <span className="font-medium truncate">{amexFile.name}</span>
                <span className="text-gray-400 text-xs ml-auto">{(amexFile.size / 1024).toFixed(1)} KB</span>
              </div>
            )}
            {amexResult && (
              <div className={`rounded-lg p-3 text-sm space-y-1 ${amexResult.insertados > 0 ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                <p className="font-medium text-green-700">Importación completada</p>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <span className="text-gray-500">Movimientos nuevos:</span>
                  <span className="font-bold text-green-600">{amexResult.insertados}</span>
                  <span className="text-gray-500">Ya existían:</span>
                  <span className="font-medium">{amexResult.omitidos_duplicados}</span>
                  <span className="text-gray-500">Total procesados:</span>
                  <span className="font-medium">{amexResult.total_procesados}</span>
                </div>
                {amexResult.errores?.length > 0 && (
                  <div className="mt-2 text-xs text-red-600">
                    {amexResult.errores.map((e, i) => <p key={i}>⚠ {e}</p>)}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Los cargos aparecen en el Cash Flow como egresos. Los pagos a AMEX se excluyen — ya están en BanBajío.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAmexDialogOpen(false); setAmexResult(null); setAmexFile(null); }}>
              {amexResult ? 'Cerrar' : 'Cancelar'}
            </Button>
            {!amexResult && (
              <Button onClick={handleAmexImport} disabled={!amexFile || importingAmex} className="bg-[#007BC1] hover:bg-[#006aab] text-white gap-2">
                {importingAmex ? <><Loader2 size={14} className="animate-spin" /> Importando...</> : <><Upload size={14} /> Importar</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankStatementsModule;
